import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  ArrowLeft, Globe, TrendingUp, TrendingDown, Users, Link2, FileText,
  Search, ExternalLink, RefreshCw, Download, Calendar, Filter,
  ChevronUp, ChevronDown, ChevronRight, AlertTriangle, CheckCircle, XCircle, Info,
  BarChart3, PieChart, Target, Zap, Shield, Eye, MousePointer,
  MessageCircle, Sparkles, Newspaper, Hash, Award, HelpCircle, TrendingUp as Trending,
  Loader2, Activity, Database, Globe2, Wifi, Server, Brain, Megaphone, Image, LayoutGrid,
  Square, RectangleHorizontal, Columns, Code, Clock, Smartphone, Pencil,
  Play, MousePointerClick, Layers, Crosshair, MessageSquare, ThumbsUp, Video
} from 'lucide-react';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart as RechartsPie, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { competitorService } from '../../services/competitorService';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import { Competitor } from '../../types';

type TabType = 'overview' | 'pages' | 'backlinks' | 'ads' | 'audit';

// Ads types
interface Ad {
  index: number;
  imageUrl: string;
  format: string;
  formatName: string;
  dimensions: string;
  type: string;
}

interface PaidKeyword {
  keyword: string;
  volume: number;
  cpc: number;
  cpcFormatted?: string;
  competition: string;
  competitionIndex: number;
}

interface AdsData {
  platform: string;
  advertiser: string;
  hasActiveAds: boolean;
  totalAdsFound: number;
  adsCollected: number;
  ads: Ad[];
  formatDistribution: Record<string, number>;
  transparencyUrl?: string;
  checkedAt: string;
  paidKeywords?: PaidKeyword[];
  extractedKeywordsCount?: number;
  hasMore?: boolean;
  nextCursor?: string | null;
  advertiserId?: string;
}

interface Region {
  code: string;
  name: string;
  flag: string;
}

// Audit types
interface AuditIssue {
  id: string;
  title: string;
  description: string;
  severity: 'critical' | 'warning' | 'info' | 'passed';
  category: string;
  recommendation: string;
  affectedUrls?: string[];
}

interface AuditCategory {
  name: string;
  icon?: React.ReactNode;
  score: number;
  issues: AuditIssue[];
  expanded?: boolean;
}

interface SEOAnalysis {
  score: number;
  loadTime: number;
  contentLength: number;
  meta: {
    title: string;
    titleLength: number;
    description: string;
    descriptionLength: number;
    keywords: string;
    robots: string;
    canonical: string;
    ogTitle: string;
    ogDescription: string;
    ogImage: string;
    twitterCard: string;
  };
  headings: {
    h1: string[];
    h2: string[];
    h3: string[];
    h4: string[];
    h1Count: number;
    h2Count: number;
    h3Count: number;
    h4Count: number;
  };
  links: {
    internalCount: number;
    externalCount: number;
    internal: Array<{ href: string; text: string; rel: string }>;
    external: Array<{ href: string; text: string; rel: string }>;
  };
  images: {
    total: number;
    withAlt: number;
    withoutAlt: number;
    list: Array<{ src: string; alt?: string; hasAlt: boolean }>;
  };
  content: {
    wordCount: number;
    paragraphs: number;
    textToHtmlRatio: number;
  };
  technical: {
    hasViewport: boolean;
    hasCharset: boolean;
    hasLangAttribute: boolean;
    usesHttps: boolean;
    hasStructuredData: boolean;
    hasFavicon: boolean;
  };
  issues: Array<{ type: string; code: string; message: string }>;
}

// Analysis step type
interface AnalysisStep {
  id: string;
  label: string;
  status: 'pending' | 'running' | 'completed' | 'error';
  icon: React.ReactNode;
  detail?: string;
}

const CompetitorDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [competitor, setCompetitor] = useState<Competitor | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [keywords, setKeywords] = useState<any[]>([]);
  const [backlinks, setBacklinks] = useState<any[]>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [keywordSearch, setKeywordSearch] = useState('');
  const [backlinkSearch, setBacklinkSearch] = useState('');
  const hasStartedAnalysis = useRef(false);

  // Ads state
  const [adsData, setAdsData] = useState<AdsData | null>(null);
  const [metaAdsData, setMetaAdsData] = useState<any>(null);
  const [isLoadingAds, setIsLoadingAds] = useState(false);
  const [isLoadingMetaAds, setIsLoadingMetaAds] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState('BR');
  const [regions, setRegions] = useState<Region[]>([]);
  const [selectedAd, setSelectedAd] = useState<Ad | null>(null);
  const [adsPlatformTab, setAdsPlatformTab] = useState<'google' | 'meta'>('google');
  const [isLoadingMoreGoogle, setIsLoadingMoreGoogle] = useState(false);
  const [isLoadingMoreMeta, setIsLoadingMoreMeta] = useState(false);

  // Site Audit state
  const [auditResult, setAuditResult] = useState<any>(null);
  const [isLoadingAudit, setIsLoadingAudit] = useState(false);
  const [auditDevice, setAuditDevice] = useState<'mobile' | 'desktop'>('mobile');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  // Analysis progress steps
  const [analysisSteps, setAnalysisSteps] = useState<AnalysisStep[]>([
    { id: 'connect', label: t('competitors.analysis.connecting', 'Conectando aos servidores'), status: 'pending', icon: <Wifi className="w-5 h-5" /> },
    { id: 'serp', label: t('competitors.analysis.serp', 'Coletando dados SERP'), status: 'pending', icon: <Search className="w-5 h-5" /> },
    { id: 'keywords', label: t('competitors.analysis.keywords', 'Analisando keywords'), status: 'pending', icon: <Hash className="w-5 h-5" /> },
    { id: 'metrics', label: t('competitors.analysis.metrics', 'Obtendo métricas do Google Ads'), status: 'pending', icon: <BarChart3 className="w-5 h-5" /> },
    { id: 'backlinks', label: t('competitors.analysis.backlinks', 'Mapeando backlinks'), status: 'pending', icon: <Link2 className="w-5 h-5" /> },
    { id: 'ads', label: t('competitors.analysis.ads', 'Buscando anúncios (Google + Meta)'), status: 'pending', icon: <Megaphone className="w-5 h-5" /> },
    { id: 'audit', label: t('competitors.analysis.audit', 'Auditando site'), status: 'pending', icon: <Shield className="w-5 h-5" /> },
    { id: 'process', label: t('competitors.analysis.processing', 'Processando resultados'), status: 'pending', icon: <Brain className="w-5 h-5" /> },
  ]);

  // Filtered data
  const filteredKeywords = keywords.filter(kw =>
    !keywordSearch ||
    kw.keyword?.toLowerCase().includes(keywordSearch.toLowerCase()) ||
    kw.url?.toLowerCase().includes(keywordSearch.toLowerCase())
  );

  const filteredBacklinks = backlinks.filter(bl =>
    !backlinkSearch ||
    bl.source_domain?.toLowerCase().includes(backlinkSearch.toLowerCase()) ||
    bl.anchor_text?.toLowerCase().includes(backlinkSearch.toLowerCase())
  );

  // Audit helper functions
  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600 dark:text-green-400';
    if (score >= 70) return 'text-yellow-600 dark:text-yellow-400';
    if (score >= 50) return 'text-orange-600 dark:text-orange-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getScoreBgColor = (score: number) => {
    if (score >= 90) return 'bg-green-500';
    if (score >= 70) return 'bg-yellow-500';
    if (score >= 50) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'info':
        return <Info className="w-5 h-5 text-blue-500" />;
      case 'passed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      default:
        return null;
    }
  };

  const toggleCategory = (categoryName: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryName)) {
        newSet.delete(categoryName);
      } else {
        newSet.add(categoryName);
      }
      return newSet;
    });
  };

  // Get current device audit data
  const getCurrentAuditData = () => {
    if (!auditResult) return null;
    const deviceData = auditDevice === 'mobile' ? auditResult.mobile : auditResult.desktop;
    if (deviceData) return deviceData;

    // Fallback
    const scores = auditDevice === 'mobile' ? auditResult.scores?.mobile : auditResult.scores?.desktop;
    const screenshot = auditDevice === 'mobile'
      ? (auditResult.mobile?.screenshot || auditResult.screenshot)
      : (auditResult.desktop?.screenshot || auditResult.screenshot);
    const coreWebVitals = auditDevice === 'mobile'
      ? (auditResult.mobile?.coreWebVitals || auditResult.coreWebVitals)
      : (auditResult.desktop?.coreWebVitals || auditResult.coreWebVitals);
    const overallScore = scores
      ? Math.round((scores.performance * 0.3) + (scores.seo * 0.3) + (scores.accessibility * 0.2) + (scores.bestPractices * 0.2))
      : auditResult.overallScore;

    return {
      overallScore,
      categories: auditResult.categories,
      totalIssues: auditResult.totalIssues,
      coreWebVitals,
      screenshot,
      scores
    };
  };

  // Export to CSV function
  const exportToCSV = (data: any[], filename: string, type: 'keywords' | 'backlinks') => {
    let csvContent = '';

    if (type === 'keywords') {
      csvContent = 'Keyword,Position,Volume,CPC,Competition,Competition Index,Trend,Traffic,Source,URL\n';
      data.forEach(item => {
        csvContent += `"${item.keyword || ''}",${item.position || ''},${item.search_volume || item.volume || 0},${item.cpc || 0},"${item.competition || ''}",${item.competitionIndex || 0},"${item.trend || ''}",${item.traffic_share || item.traffic || 0},"${item.source || 'estimated'}","${item.url || ''}"\n`;
      });
    } else {
      csvContent = 'Source Domain,Source URL,Target URL,Anchor Text,DA,Type,First Seen\n';
      data.forEach(item => {
        csvContent += `"${item.source_domain || ''}","${item.source_url || ''}","${item.target_url || ''}","${item.anchor_text || ''}",${item.domain_authority || 0},"${item.link_type || ''}","${item.first_seen || ''}"\n`;
      });
    }

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  useEffect(() => {
    if (id) {
      loadCompetitor();
    }
    // Load regions on mount for the country selector
    loadRegions();
  }, [id]);

  // Update selectedRegion when competitor is loaded
  useEffect(() => {
    if (competitor?.country) {
      setSelectedRegion(competitor.country);
    }
  }, [competitor]);

  // Auto-start analysis if URL parameter is present
  useEffect(() => {
    const shouldAnalyze = searchParams.get('analyze') === 'true';
    if (shouldAnalyze && competitor && !hasStartedAnalysis.current && !isAnalyzing) {
      hasStartedAnalysis.current = true;
      // Remove the parameter from URL
      setSearchParams({}, { replace: true });
      handleAnalyze();
    }
  }, [competitor, searchParams]);

  useEffect(() => {
    if (id && activeTab === 'pages') loadKeywords();
    if (id && activeTab === 'backlinks') loadBacklinks();
  }, [id, activeTab]);

  const loadRegions = async () => {
    try {
      const response = await competitorService.getAdsRegions();
      if (response.success && response.data) {
        setRegions(response.data);
      }
    } catch (error) {
      console.error('Error loading regions:', error);
    }
  };

  const loadAds = async (maxAdsOverride?: number) => {
    if (!competitor?.domain) return;
    setIsLoadingAds(true);
    // Keep existing adsData while loading - don't clear it
    try {
      // Import polling service
      const { pollJobUntilComplete } = await import('../../services/aiJobService');

      const response = await competitorService.exploreCompetitorAds(competitor.domain, {
        region: selectedRegion,
        maxAds: maxAdsOverride || 20,
        async: true
      });

      let result = null;

      // If async mode, poll for result
      if (response.async && response.jobId) {
        toast.loading(t('competitors.loadingAds', 'Fetching ads...'), { id: 'ads' });

        result = await pollJobUntilComplete(
          response.jobId,
          undefined,
          2000,
          120000 // 2 minute timeout
        );

        toast.dismiss('ads');
      } else if (response.success && response.data) {
        result = response.data;
      }

      if (result) {
        setAdsData(result);

        // Persist to DB so data survives page reload
        try {
          // Preserve existing Meta ads data when saving Google ads
          const savedAdsData: any = { ...result };
          if (metaAdsData) {
            savedAdsData.meta = metaAdsData;
          }
          await competitorService.updateAnalysisAdsData(id!, {
            ads_data: savedAdsData,
            paid_keywords_list: result.paidKeywords || [],
            paid_traffic_value: (result.paidKeywords || []).reduce((sum: number, kw: any) => {
              const cpc = (kw.cpc || 0) > 1000 ? (kw.cpc || 0) / 1000000 : (kw.cpc || 0);
              const monthlyClicks = (kw.volume || 0) * 0.02;
              return sum + (monthlyClicks * cpc);
            }, 0),
            has_active_ads: result.hasActiveAds || false
          });
        } catch (saveError) {
          console.error('Error saving ads data:', saveError);
        }
      }
    } catch (error) {
      console.error('Error loading ads:', error);
      toast.dismiss('ads');
      toast.error(t('errors.generic', 'Error fetching ads'));
      // Keep existing adsData on error - don't clear it
    } finally {
      setIsLoadingAds(false);
    }
  };

  const loadMetaAds = async () => {
    if (!competitor?.domain) return;
    setIsLoadingMetaAds(true);
    try {
      const { pollJobUntilComplete } = await import('../../services/aiJobService');

      const response = await competitorService.exploreMetaAds(competitor.domain, {
        countryCode: selectedRegion,
        maxAds: 20,
        async: true
      });

      let result = null;

      if (response.async && response.jobId) {
        toast.loading(t('competitors.loadingMetaAds', 'Fetching Meta ads...'), { id: 'meta-ads' });

        result = await pollJobUntilComplete(
          response.jobId,
          undefined,
          2000,
          120000
        );

        toast.dismiss('meta-ads');
      } else if (response.success && response.data) {
        result = response.data;
      }

      if (result) {
        setMetaAdsData(result);

        // Persist Meta ads data to DB so it survives page reload
        try {
          const currentResponse = await competitorService.getCompetitor(id!);
          const latestAnalysis = currentResponse.data?.analyses?.[0] as any;
          const currentAdsData = latestAnalysis?.ads_data || {};

          await competitorService.updateAnalysisAdsData(id!, {
            ads_data: {
              ...currentAdsData,
              meta: result
            }
          });
        } catch (saveError) {
          console.error('Error saving Meta ads data:', saveError);
        }
      }
    } catch (error) {
      console.error('Error loading Meta ads:', error);
      toast.dismiss('meta-ads');
      toast.error(t('errors.generic', 'Error fetching Meta ads'));
    } finally {
      setIsLoadingMetaAds(false);
    }
  };

  const runSiteAudit = async () => {
    if (!competitor?.domain) return;
    setIsLoadingAudit(true);
    setAuditResult(null);
    try {
      // Import polling service
      const { pollJobUntilComplete } = await import('../../services/aiJobService');

      const url = `https://${competitor.domain}`;
      const response = await competitorService.runSiteAudit(url, true, true);

      let result = null;

      // If async mode, poll for result
      if (response.async && response.jobId) {
        toast.loading(t('competitors.runningAudit', 'Running audit...'), { id: 'audit' });

        result = await pollJobUntilComplete(
          response.jobId,
          undefined,
          2000,
          180000 // 3 minute timeout
        );

        toast.dismiss('audit');
        toast.success(t('competitors.auditComplete', 'Audit completed!'));
      } else if (response.success && response.data) {
        result = response.data;
      }

      if (result) {
        setAuditResult(result);
      }
    } catch (error: any) {
      console.error('Error running site audit:', error);
      toast.dismiss('audit');
      toast.error(error.message || t('competitors.auditError', 'Error in audit'));
    } finally {
      setIsLoadingAudit(false);
    }
  };

  const loadCompetitor = async () => {
    try {
      const response = await competitorService.getCompetitor(id!);
      if (response.success && response.data) {
        setCompetitor(response.data);

        // Load ads data from latest analysis if available
        const latestAnalysis = response.data.analyses?.[0] as any;
        if (latestAnalysis?.ads_data || latestAnalysis?.paid_keywords_list) {
          // Check if ads_data contains the Google data directly or nested under a key
          const googleAdsRaw = latestAnalysis.ads_data;
          const hasGoogleData = googleAdsRaw?.platform === 'google_ads_transparency' || googleAdsRaw?.ads?.length > 0 || latestAnalysis.paid_keywords_list?.length > 0;

          if (hasGoogleData) {
            setAdsData({
              platform: 'google_ads_transparency',
              advertiser: response.data.name,
              hasActiveAds: latestAnalysis.has_active_ads || false,
              totalAdsFound: googleAdsRaw?.totalAdsFound || 0,
              adsCollected: googleAdsRaw?.ads?.length || 0,
              ads: googleAdsRaw?.ads || [],
              formatDistribution: googleAdsRaw?.formatDistribution || {},
              transparencyUrl: googleAdsRaw?.transparencyUrl,
              checkedAt: googleAdsRaw?.checkedAt || latestAnalysis.createdAt,
              paidKeywords: latestAnalysis.paid_keywords_list || googleAdsRaw?.paidKeywords || [],
              extractedKeywordsCount: latestAnalysis.paid_keywords_list?.length || 0,
              // ?? em vez de || pra preservar `undefined` em análises legadas
              // (sem o cursor salvo) e deixar o botão visível.
              hasMore: googleAdsRaw?.hasMore ?? undefined,
              nextCursor: googleAdsRaw?.nextCursor ?? null,
              advertiserId: googleAdsRaw?.advertiserId,
            });
          }

          // Load Meta ads data if saved
          const metaRaw = googleAdsRaw?.meta;
          if (metaRaw) {
            setMetaAdsData(metaRaw);
          }
        }

        // Load latest audit for this domain
        try {
          const auditResponse = await competitorService.getLatestAuditByUrl(response.data.domain);
          if (auditResponse.success && auditResponse.data) {
            setAuditResult(auditResponse.data);
          }
        } catch (auditError) {
          console.error('Error loading audit:', auditError);
        }
      }
    } catch (error) {
      console.error('Error loading competitor:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoadMoreGoogleAds = async () => {
    if (!id || isLoadingMoreGoogle) return;
    setIsLoadingMoreGoogle(true);
    try {
      const submit = await competitorService.loadMoreGoogleAds(id, 20);
      const submitAny = submit as any;

      if (submitAny.async && submitAny.jobId) {
        const { pollJobUntilComplete } = await import('../../services/aiJobService');
        const jobResult = await pollJobUntilComplete(submitAny.jobId, undefined, 2500, 180000);
        const data = jobResult || {};
        const newCount = (data.newAds || []).length;
        setAdsData(prev => prev ? {
          ...prev,
          ads: [...(prev.ads || []), ...(data.newAds || [])],
          adsCollected: (prev.ads?.length || 0) + newCount,
          hasMore: data.hasMore,
          nextCursor: data.nextCursor || null,
        } : prev);
        if (newCount === 0) {
          toast.success(t('competitors.allGoogleAdsLoaded', 'Todos os anúncios disponíveis na Transparência do Google já foram carregados.'));
        } else {
          toast.success(`${newCount} ${t('competitors.newAdsLoaded', 'novos anúncios carregados')}`);
        }
      } else if (submit.success && submit.data) {
        const newCount = (submit.data.newAds || []).length;
        setAdsData(prev => prev ? {
          ...prev,
          ads: [...(prev.ads || []), ...(submit.data!.newAds || [])],
          adsCollected: submit.data!.totalAdsNow,
          hasMore: submit.data!.hasMore,
        } : prev);
        if (newCount === 0) {
          toast.success(t('competitors.allGoogleAdsLoaded', 'Todos os anúncios disponíveis na Transparência do Google já foram carregados.'));
        }
      }
    } catch (err: any) {
      console.error('Load more Google ads error:', err);
      toast.error(err?.response?.data?.message || err?.message || t('competitors.loadMoreError', 'Erro ao carregar mais anúncios'));
    } finally {
      setIsLoadingMoreGoogle(false);
    }
  };

  const handleLoadMoreMetaAds = async () => {
    if (!id || isLoadingMoreMeta) return;
    setIsLoadingMoreMeta(true);
    try {
      const submit = await competitorService.loadMoreMetaAds(id, 20);
      const submitAny = submit as any;
      if (submitAny.async && submitAny.jobId) {
        const { pollJobUntilComplete } = await import('../../services/aiJobService');
        const jobResult = await pollJobUntilComplete(submitAny.jobId, undefined, 3000, 180000);
        const data = jobResult || {};
        const newCount = (data.newAds || []).length;
        setMetaAdsData((prev: any) => prev ? {
          ...prev,
          ads: [...(prev.ads || []), ...(data.newAds || [])],
          adsCollected: (prev.ads?.length || 0) + newCount,
          hasMore: data.hasMore,
        } : prev);
        // Toast explícito pra evitar confusão: sem novos + hasMore=false é fim da lista
        if (newCount === 0) {
          toast.success(t('competitors.allMetaAdsLoaded', 'Todos os anúncios disponíveis na Biblioteca do Meta já foram carregados.'));
        } else {
          toast.success(`${newCount} ${t('competitors.newAdsLoaded', 'novos anúncios carregados')}`);
        }
      } else if (submit.success && submit.data) {
        const newCount = (submit.data.newAds || []).length;
        setMetaAdsData((prev: any) => prev ? {
          ...prev,
          ads: [...(prev.ads || []), ...(submit.data!.newAds || [])],
          adsCollected: submit.data!.totalAdsNow,
          hasMore: submit.data!.hasMore,
        } : prev);
        if (newCount === 0) {
          toast.success(t('competitors.allMetaAdsLoaded', 'Todos os anúncios disponíveis na Biblioteca do Meta já foram carregados.'));
        }
      }
    } catch (err: any) {
      console.error('Load more Meta ads error:', err);
      toast.error(err?.response?.data?.message || err?.message || t('competitors.loadMoreError', 'Erro ao carregar mais anúncios'));
    } finally {
      setIsLoadingMoreMeta(false);
    }
  };

  const loadKeywords = async () => {
    try {
      const response = await competitorService.getKeywords(id!);
      if (response.success) setKeywords(response.data || []);
    } catch (error) {
      console.error('Error loading keywords:', error);
    }
  };

  const loadBacklinks = async () => {
    try {
      const response = await competitorService.getBacklinks(id!);
      if (response.success) setBacklinks(response.data || []);
    } catch (error) {
      console.error('Error loading backlinks:', error);
    }
  };

  const updateStep = (stepId: string, status: AnalysisStep['status'], detail?: string) => {
    setAnalysisSteps(prev => prev.map(step =>
      step.id === stepId ? { ...step, status, detail } : step
    ));
  };

  const handleAnalyze = async () => {
    setIsAnalyzing(true);

    // Reset steps
    setAnalysisSteps(prev => prev.map(step => ({ ...step, status: 'pending' as const, detail: undefined })));

    try {
      // Import polling service
      const { pollJobUntilComplete } = await import('../../services/aiJobService');

      // Step 1: Connecting
      updateStep('connect', 'running');
      await new Promise(r => setTimeout(r, 800));
      updateStep('connect', 'completed', t('competitors.analysis.connected', 'Conectado'));

      // Step 2: SERP - Start background analysis
      updateStep('serp', 'running');

      // Make the actual API call with async mode
      const analyzeResponse = await competitorService.analyze(id!, 'full', true);

      // If async mode, poll for result
      let analyzeResult: any = null;
      if (analyzeResponse.async && analyzeResponse.jobId) {
        updateStep('serp', 'completed', t('competitors.analysis.serpDone', 'Dados coletados'));

        // Step 3: Keywords
        updateStep('keywords', 'running');

        // Poll for analysis completion with progress updates
        analyzeResult = await pollJobUntilComplete(
          analyzeResponse.jobId,
          (progress) => {
            // Update steps based on progress
            if (progress >= 20 && progress < 40) {
              updateStep('keywords', 'completed', t('competitors.analysis.keywordsDone', 'Keywords identificadas'));
              updateStep('metrics', 'running');
            } else if (progress >= 40 && progress < 60) {
              updateStep('metrics', 'completed', t('competitors.analysis.metricsDone', 'Métricas obtidas'));
              updateStep('backlinks', 'running');
            } else if (progress >= 60 && progress < 80) {
              updateStep('backlinks', 'completed', t('competitors.analysis.backlinksDone', 'Backlinks mapeados'));
              updateStep('ads', 'running');
            } else if (progress >= 80) {
              updateStep('ads', 'completed', t('competitors.analysis.adsDone', 'Anúncios coletados'));
              updateStep('audit', 'running');
            }
          },
          2000,
          300000 // 5 minute timeout for competitor analysis
        );
      } else {
        analyzeResult = analyzeResponse.data;
      }

      // Mark remaining steps as completed
      updateStep('keywords', 'completed', t('competitors.analysis.keywordsDone', 'Keywords identificadas'));
      updateStep('metrics', 'completed', t('competitors.analysis.metricsDone', 'Métricas obtidas'));
      updateStep('backlinks', 'completed', t('competitors.analysis.backlinksDone', 'Backlinks mapeados'));
      updateStep('ads', 'completed', t('competitors.analysis.adsDone', 'Anúncios coletados'));

      // Run site audit and ads exploration in background (non-blocking)
      // These run independently and update the UI when they finish
      if (competitor?.domain) {
        // Fire and forget - don't await
        (async () => {
          try {
            const [auditJobResponse, adsJobResponse, metaAdsJobResponse] = await Promise.all([
              competitorService.runSiteAudit(`https://${competitor.domain}`, true, true).catch(err => {
                console.error('Error starting site audit:', err);
                return null;
              }),
              competitorService.exploreCompetitorAds(competitor.domain, { region: selectedRegion, maxAds: 20, async: true }).catch(err => {
                console.error('Error starting ads exploration:', err);
                return null;
              }),
              competitorService.exploreMetaAds(competitor.domain, { countryCode: selectedRegion, maxAds: 20, async: true }).catch(err => {
                console.error('Error starting Meta ads exploration:', err);
                return null;
              })
            ]);

            const pollPromises: Promise<any>[] = [];

            if (auditJobResponse?.async && auditJobResponse?.jobId) {
              pollPromises.push(
                pollJobUntilComplete(auditJobResponse.jobId, undefined, 2000, 180000)
                  .then(result => ({ type: 'audit', data: result }))
                  .catch(err => ({ type: 'audit', error: err }))
              );
            } else if (auditJobResponse?.success && auditJobResponse?.data) {
              pollPromises.push(Promise.resolve({ type: 'audit', data: auditJobResponse.data }));
            }

            if (adsJobResponse?.async && adsJobResponse?.jobId) {
              pollPromises.push(
                pollJobUntilComplete(adsJobResponse.jobId, undefined, 2000, 120000)
                  .then(result => ({ type: 'ads', data: result }))
                  .catch(err => ({ type: 'ads', error: err }))
              );
            } else if (adsJobResponse?.success && adsJobResponse?.data) {
              pollPromises.push(Promise.resolve({ type: 'ads', data: adsJobResponse.data }));
            }

            if (metaAdsJobResponse?.async && metaAdsJobResponse?.jobId) {
              pollPromises.push(
                pollJobUntilComplete(metaAdsJobResponse.jobId, undefined, 2000, 120000)
                  .then(result => ({ type: 'meta-ads', data: result }))
                  .catch(err => ({ type: 'meta-ads', error: err }))
              );
            } else if (metaAdsJobResponse?.success && metaAdsJobResponse?.data) {
              pollPromises.push(Promise.resolve({ type: 'meta-ads', data: metaAdsJobResponse.data }));
            }

            const pollResults = await Promise.all(pollPromises);

            for (const result of pollResults) {
              if (result.type === 'ads' && result.data && !result.error) {
                setAdsData(result.data);
                try {
                  await competitorService.updateAnalysisAdsData(id!, {
                    ads_data: result.data,
                    paid_keywords_list: result.data.paidKeywords || [],
                    paid_traffic_value: result.data.paidKeywords?.reduce((sum: number, kw: any) => {
                      const cpc = (kw.cpc || 0) > 1000 ? (kw.cpc || 0) / 1000000 : (kw.cpc || 0);
                      const monthlyClicks = (kw.volume || 0) * 0.02;
                      return sum + (monthlyClicks * cpc);
                    }, 0) || 0,
                    has_active_ads: result.data.hasActiveAds || false
                  });
                } catch (saveError) {
                  console.error('Error saving ads data:', saveError);
                }
              }

              if (result.type === 'meta-ads' && result.data && !result.error) {
                setMetaAdsData(result.data);
                try {
                  const currentResp = await competitorService.getCompetitor(id!);
                  const currentAnalysis = currentResp.data?.analyses?.[0] as any;
                  const currentAdsData = currentAnalysis?.ads_data || {};
                  await competitorService.updateAnalysisAdsData(id!, {
                    ads_data: { ...currentAdsData, meta: result.data }
                  });
                } catch (saveErr) {
                  console.error('Error saving Meta ads:', saveErr);
                }
              }

              if (result.type === 'audit' && result.data && !result.error) {
                setAuditResult(result.data);
              }
            }
          } catch (bgError) {
            console.error('Background jobs error:', bgError);
          }
        })();
      }

      updateStep('audit', 'completed', t('competitors.analysis.auditDone', 'Processando em background'));

      // Step 8: Processing complete
      updateStep('process', 'running');

      if (analyzeResult) {
        updateStep('process', 'completed', t('competitors.analysis.complete', 'Análise concluída!'));

        // Reload all data after analysis completes
        await loadCompetitor();
        loadKeywords();
        loadBacklinks();

        // Small delay before hiding the analysis box
        await new Promise(r => setTimeout(r, 1500));
      } else {
        updateStep('process', 'error', t('competitors.analysis.error', 'Erro no processamento'));
      }
    } catch (error) {
      console.error('Error analyzing:', error);
      // Mark current running step as error
      setAnalysisSteps(prev => prev.map(step =>
        step.status === 'running' ? { ...step, status: 'error' as const, detail: t('competitors.analysis.error', 'Erro') } : step
      ));
    } finally {
      setIsAnalyzing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-3 sm:space-y-6 animate-pulse">
        <div className="h-8 w-48 bg-gray-200 dark:bg-gray-700 rounded" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
          {[1,2,3,4].map(i => <div key={i} className="h-24 bg-gray-200 dark:bg-gray-700 rounded-xl" />)}
        </div>
        <div className="h-96 bg-gray-200 dark:bg-gray-700 rounded-xl" />
      </div>
    );
  }

  if (!competitor) {
    return (
      <div className="text-center py-12">
        <Globe className="w-16 h-16 mx-auto text-gray-300 mb-4" />
        <p className="text-gray-500 mb-4">{t('competitors.notFound', 'Concorrente não encontrado')}</p>
        <Link to="/competitors">
          <Button>{t('common.back', 'Voltar')}</Button>
        </Link>
      </div>
    );
  }

  const latestAnalysis = competitor.analyses?.[0] as any;

  // Traffic sources - only show real data we have
  const trafficSources = [
    { name: 'Orgânico', value: competitor.organic_traffic || 0, color: '#10B981' },
    { name: 'Pago', value: competitor.paid_traffic || 0, color: '#6366F1' },
  ].filter(s => s.value > 0);

  // Check if we have any traffic data
  const hasTrafficData = (competitor.organic_traffic || 0) > 0 || (competitor.paid_traffic || 0) > 0;

  const tabs = [
    { id: 'overview', label: t('competitors.overview', 'Visão Geral'), icon: BarChart3 },
    { id: 'pages', label: t('competitors.pages', 'Páginas'), icon: FileText },
    { id: 'backlinks', label: t('competitors.backlinks', 'Backlinks'), icon: Link2 },
    { id: 'ads', label: t('competitors.paidAds', 'Anúncios Pagos'), icon: Megaphone },
    { id: 'audit', label: t('competitors.siteAudit', 'Site Audit'), icon: Shield },
  ];

  const formatNumber = (num: number) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toString() || '0';
  };

  const formatCurrency = (num: number) => {
    if (num >= 1000000) return (num / 1000000).toFixed(2) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(2) + 'K';
    return num.toFixed(2);
  };

  // Coerção defensiva — alguns ads legados gravaram dimensions/formatName como
  // objeto ({ width, height }) em vez de string. JSX renderizava "[object Object]".
  // Esse helper extrai um label legível em qualquer caso.
  const safeStr = (v: any): string => {
    if (v == null) return '';
    if (typeof v === 'string' || typeof v === 'number') return String(v);
    if (typeof v === 'object') {
      // Padrão {width, height} de scrapes antigos
      if (v.width != null && v.height != null) return `${v.width}x${v.height}`;
      // Outros padrões comuns
      if (v.value != null) return String(v.value);
      if (v.text != null) return String(v.text);
      if (v.name != null) return String(v.name);
      if (v.label != null) return String(v.label);
      // Último recurso: tenta primeira propriedade string ou empty
      const firstStr = Object.values(v).find((x) => typeof x === 'string' || typeof x === 'number');
      return firstStr != null ? String(firstStr) : '';
    }
    return String(v);
  };

  return (
    <div className="space-y-3 sm:space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 md:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3 md:gap-4 min-w-0">
            <Link to="/competitors" className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors flex-shrink-0">
              <ArrowLeft className="w-5 h-5 text-gray-500" />
            </Link>
            <div className="w-10 h-10 md:w-14 md:h-14 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
              <Globe className="w-5 h-5 md:w-7 md:h-7 text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="text-lg md:text-2xl font-bold text-gray-900 dark:text-white truncate">{competitor.name}</h1>
              <div className="flex items-center gap-2 md:gap-3 mt-1 flex-wrap">
                <a
                  href={`https://${competitor.domain}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm md:text-base text-indigo-600 hover:text-indigo-700 flex items-center gap-1 truncate"
                >
                  {competitor.domain}
                  <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" />
                </a>
                {competitor.industry && (
                  <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs rounded-full">
                    {competitor.industry}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 md:gap-3 ml-auto sm:ml-0 flex-shrink-0">
            {/* Country Selector */}
            <div className="flex items-center gap-1.5 md:gap-2">
              <Globe2 className="w-4 h-4 text-gray-500 hidden sm:block" />
              <select
                value={selectedRegion}
                onChange={(e) => setSelectedRegion(e.target.value)}
                className="px-2 md:px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors max-w-[120px] md:max-w-none"
              >
                {regions.map((region) => (
                  <option key={region.code} value={region.code}>
                    {region.flag} {region.name}
                  </option>
                ))}
              </select>
            </div>
            <Button
              size="sm"
              onClick={handleAnalyze}
              disabled={isAnalyzing}
              className={`
                ${isAnalyzing
                  ? 'bg-gradient-to-r from-indigo-600 to-purple-600'
                  : 'bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 hover:from-indigo-700 hover:via-purple-700 hover:to-pink-600 shadow-lg shadow-purple-500/30 hover:shadow-purple-500/50 hover:scale-105 transition-all duration-300 animate-pulse hover:animate-none'
                }
                text-white font-semibold px-3 md:px-5
              `}
            >
              {isAnalyzing ? (
                <Activity className="w-4 h-4 mr-1.5 md:mr-2 animate-spin" />
              ) : (
                <BarChart3 className="w-4 h-4 mr-1.5 md:mr-2" />
              )}
              <span className="hidden sm:inline">{isAnalyzing ? t('competitors.analyzing', 'Analisando...') : t('competitors.analyze', 'Analisar')}</span>
              <span className="sm:hidden">{isAnalyzing ? '...' : t('competitors.analyze', 'Analisar')}</span>
            </Button>
          </div>
        </div>

        {/* Analysis Progress Box */}
        {isAnalyzing && (
          <div className="mt-6 overflow-hidden">
            <div className="bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 dark:from-indigo-900/30 dark:via-purple-900/30 dark:to-pink-900/30 rounded-2xl border border-indigo-200 dark:border-indigo-800 p-4 sm:p-6">
              <div className="flex items-center gap-3 mb-4 sm:mb-6">
                <div className="relative">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                    <Activity className="w-6 h-6 text-white animate-pulse" />
                  </div>
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white dark:border-gray-800 animate-ping" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {t('competitors.analysis.title', 'Análise em Andamento')}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t('competitors.analysis.subtitle', 'Coletando dados de múltiplas fontes...')}
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                {analysisSteps.map((step, index) => (
                  <div
                    key={step.id}
                    className={`flex items-center gap-3 sm:gap-4 p-3 rounded-xl transition-all duration-500 ${
                      step.status === 'running'
                        ? 'bg-white dark:bg-gray-800 shadow-md scale-[1.02]'
                        : step.status === 'completed'
                        ? 'bg-green-50 dark:bg-green-900/20'
                        : step.status === 'error'
                        ? 'bg-red-50 dark:bg-red-900/20'
                        : 'opacity-50'
                    }`}
                  >
                    <div className={`w-10 h-10 flex-shrink-0 rounded-lg flex items-center justify-center ${
                      step.status === 'running'
                        ? 'bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400'
                        : step.status === 'completed'
                        ? 'bg-green-100 dark:bg-green-900/50 text-green-600 dark:text-green-400'
                        : step.status === 'error'
                        ? 'bg-red-100 dark:bg-red-900/50 text-red-600 dark:text-red-400'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-400'
                    }`}>
                      {step.status === 'running' ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : step.status === 'completed' ? (
                        <CheckCircle className="w-5 h-5" />
                      ) : step.status === 'error' ? (
                        <XCircle className="w-5 h-5" />
                      ) : (
                        step.icon
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className={`font-medium text-sm sm:text-base ${
                          step.status === 'running'
                            ? 'text-indigo-700 dark:text-indigo-300'
                            : step.status === 'completed'
                            ? 'text-green-700 dark:text-green-300'
                            : step.status === 'error'
                            ? 'text-red-700 dark:text-red-300'
                            : 'text-gray-500 dark:text-gray-400'
                        }`}>
                          {step.label}
                        </span>
                        {step.detail && (
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            step.status === 'completed'
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                              : step.status === 'error'
                              ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                          }`}>
                            {step.detail}
                          </span>
                        )}
                      </div>
                      {step.status === 'running' && (
                        <div className="mt-2 h-1 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full animate-pulse" style={{ width: '70%' }} />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

            </div>
          </div>
        )}

        {/* Quick Stats */}
        <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 sm:gap-4 mt-4 sm:mt-6">
          <div className="text-center p-2 sm:p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg min-w-0">
            <div className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">
              {competitor.domain_authority || 0}
            </div>
            <div className="text-[11px] sm:text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">Domain Authority</div>
          </div>
          <div className="text-center p-2 sm:p-4 bg-green-50 dark:bg-green-900/20 rounded-lg min-w-0">
            <div className="text-lg sm:text-2xl font-bold text-green-600">
              {formatNumber(competitor.organic_traffic || 0)}
            </div>
            <div className="text-[11px] sm:text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">Tráfego Orgânico</div>
          </div>
          <div className="text-center p-2 sm:p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg min-w-0">
            <div className="text-lg sm:text-2xl font-bold text-indigo-600">
              {formatNumber(competitor.paid_traffic || 0)}
            </div>
            <div className="text-[11px] sm:text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">Tráfego Pago</div>
          </div>
          <div className="text-center p-2 sm:p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg min-w-0">
            <div className="text-lg sm:text-2xl font-bold text-orange-600">
              {formatNumber(competitor.backlinks_count || 0)}
            </div>
            <div className="text-[11px] sm:text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">Backlinks</div>
          </div>
          <div className="text-center p-2 sm:p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg min-w-0">
            <div className="text-lg sm:text-2xl font-bold text-purple-600">
              {formatNumber(latestAnalysis?.total_keywords || latestAnalysis?.top_keywords?.length || 0)}
            </div>
            <div className="text-[11px] sm:text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">Keywords</div>
          </div>
          <div className="text-center p-2 sm:p-4 bg-pink-50 dark:bg-pink-900/20 rounded-lg min-w-0">
            <div className="text-lg sm:text-2xl font-bold text-pink-600">
              {latestAnalysis?.referring_domains || 0}
            </div>
            <div className="text-[11px] sm:text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">Ref. Domains</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="border-b border-gray-200 dark:border-gray-700">
          {/* Celular: 5 abas lado a lado (ícone em cima, nome curto embaixo), sem rolagem */}
          <nav className="grid grid-cols-5 gap-1 p-1.5 sm:flex sm:gap-1 sm:p-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as TabType)}
                  className={`flex flex-col sm:flex-row items-center justify-center gap-0.5 sm:gap-1.5 md:gap-2 px-1 sm:px-3 md:px-4 py-1.5 sm:py-2 md:py-2.5 text-[11px] sm:text-xs md:text-sm font-medium rounded-lg transition-colors whitespace-nowrap min-w-0 ${
                    activeTab === tab.id
                      ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  <span className="hidden sm:inline">{tab.label}</span>
                  <span className="sm:hidden truncate max-w-full">{tab.id === 'overview' ? 'Geral' : tab.id === 'pages' ? 'Páginas' : tab.id === 'backlinks' ? 'Links' : tab.id === 'ads' ? 'Anúncios' : 'Site'}</span>
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-4 md:p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-3 sm:space-y-6">
              {/* SERP Visibility Score */}
              {latestAnalysis && (
                <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 rounded-2xl p-4 md:p-6 text-white">
                  <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <h3 className="text-base md:text-lg font-semibold mb-1 flex items-center gap-2">
                        <Sparkles className="w-5 h-5 flex-shrink-0" />
                        {t('competitors.serpVisibilityScore', 'SERP Visibility Score')}
                      </h3>
                      <p className="text-xs md:text-sm text-white/70">{t('competitors.serpVisibilityDesc', 'Visibilidade geral nos resultados do Google')}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-3xl md:text-5xl font-bold">
                        {(() => {
                          let score = 0;
                          // Pontos por Domain Authority (max 25)
                          score += Math.min(25, Math.round((latestAnalysis.domain_authority || 0) / 4));
                          // Pontos por tráfego orgânico (max 20)
                          const traffic = latestAnalysis.organic_traffic || 0;
                          if (traffic > 50000) score += 20;
                          else if (traffic > 10000) score += 15;
                          else if (traffic > 1000) score += 10;
                          else if (traffic > 100) score += 5;
                          // Pontos por Knowledge Graph (10)
                          if (latestAnalysis.knowledge_graph) score += 10;
                          // Pontos por Featured Snippet (10)
                          if (latestAnalysis.featured_snippet) score += 10;
                          // Pontos por posição #1 para marca (10)
                          if (latestAnalysis.brand_serp_presence?.owns_position_1) score += 10;
                          // Pontos por notícias (max 10)
                          score += Math.min(10, (latestAnalysis.news_mentions?.length || 0) * 2);
                          // Pontos por People Also Ask (max 5)
                          score += Math.min(5, (latestAnalysis.people_also_ask?.length || 0));
                          // Pontos por backlinks (max 10)
                          const backlinks = latestAnalysis.backlinks || 0;
                          if (backlinks > 1000) score += 10;
                          else if (backlinks > 100) score += 7;
                          else if (backlinks > 10) score += 4;
                          return Math.min(100, score);
                        })()}
                      </div>
                      <span className="text-sm text-white/70">/100</span>
                    </div>
                  </div>
                  {/* Progress indicators */}
                  <div className="mt-4 md:mt-6 grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
                    <div className="bg-white/10 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        {latestAnalysis.brand_serp_presence?.owns_position_1 ? (
                          <CheckCircle className="w-4 h-4 text-green-300" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-300" />
                        )}
                        <span className="text-xs">{t('competitors.dominatesBrand', 'Domina Marca')}</span>
                      </div>
                      <span className="text-sm font-semibold">
                        {latestAnalysis.brand_serp_presence?.owns_position_1 ? t('competitors.yes', 'Sim') : t('competitors.no', 'Não')}
                      </span>
                    </div>
                    <div className="bg-white/10 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        {latestAnalysis.knowledge_graph ? (
                          <CheckCircle className="w-4 h-4 text-green-300" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-300" />
                        )}
                        <span className="text-xs">{t('competitors.knowledgePanel', 'Knowledge Panel')}</span>
                      </div>
                      <span className="text-sm font-semibold">
                        {latestAnalysis.knowledge_graph ? t('competitors.has', 'Possui') : t('competitors.doesNotHave', 'Não possui')}
                      </span>
                    </div>
                    <div className="bg-white/10 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        {latestAnalysis.featured_snippet ? (
                          <CheckCircle className="w-4 h-4 text-green-300" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-300" />
                        )}
                        <span className="text-xs">{t('competitors.featuredSnippet', 'Featured Snippet')}</span>
                      </div>
                      <span className="text-sm font-semibold">
                        {latestAnalysis.featured_snippet ? t('competitors.appears', 'Aparece') : t('competitors.doesNotAppear', 'Não aparece')}
                      </span>
                    </div>
                    <div className="bg-white/10 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <Newspaper className="w-4 h-4" />
                        <span className="text-xs">{t('competitors.newsMentions', 'Notícias')}</span>
                      </div>
                      <span className="text-sm font-semibold">
                        {latestAnalysis.news_mentions?.length || 0} {t('competitors.mentions', 'menções')}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* SEO Overview */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-6">
                {/* SEO Metrics */}
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 sm:p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    {t('competitors.seoMetrics', 'Métricas SEO')}
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center">
                          <Award className="w-5 h-5 text-indigo-600" />
                        </div>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Domain Authority</span>
                      </div>
                      <span className="text-2xl font-bold text-indigo-600">
                        {latestAnalysis?.domain_authority || competitor.domain_authority || 0}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                          <FileText className="w-5 h-5 text-green-600" />
                        </div>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Páginas Indexadas</span>
                      </div>
                      <span className="text-2xl font-bold text-green-600">
                        {formatNumber(latestAnalysis?.top_keywords?.length || 0)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
                          <Link2 className="w-5 h-5 text-orange-600" />
                        </div>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Backlinks</span>
                      </div>
                      <span className="text-2xl font-bold text-orange-600">
                        {formatNumber(latestAnalysis?.backlinks || competitor.backlinks_count || 0)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                          <Globe className="w-5 h-5 text-purple-600" />
                        </div>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Ref. Domains</span>
                      </div>
                      <span className="text-2xl font-bold text-purple-600">
                        {formatNumber(latestAnalysis?.referring_domains || 0)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Data Quality Indicator */}
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 sm:p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    {t('competitors.dataQuality', 'Qualidade dos Dados')}
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg">
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-green-500" />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Keywords</span>
                      </div>
                      <span className="text-sm font-medium text-green-600">
                        {latestAnalysis?.top_keywords?.length || 0} {t('competitors.verified', 'verificadas')}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg">
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-green-500" />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Backlinks</span>
                      </div>
                      <span className="text-sm font-medium text-green-600">
                        {latestAnalysis?.backlinks || competitor.backlinks_count || 0} {t('competitors.found', 'encontrados')}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg">
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-green-500" />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Domain Authority</span>
                      </div>
                      <span className="text-sm font-medium text-green-600">
                        {latestAnalysis?.domain_authority || competitor.domain_authority || 0}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Info className="w-5 h-5 text-amber-500" />
                        <span className="text-sm text-gray-700 dark:text-gray-300">{t('competitors.trafficEstimate', 'Tráfego')}</span>
                      </div>
                      <span className="text-sm font-medium text-amber-600">
                        {t('competitors.ctrBased', 'Estimado via CTR')}
                      </span>
                    </div>
                    {latestAnalysis && (
                      <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                        <div className="flex items-center gap-2 text-blue-700 dark:text-blue-300 text-xs">
                          <Calendar className="w-4 h-4" />
                          <span>{t('competitors.lastAnalysis', 'Última análise')}: {new Date(latestAnalysis.date || latestAnalysis.created_at).toLocaleDateString('pt-BR')}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Top Keywords & Pages */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-6">
                {/* Top Keywords */}
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 sm:p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {t('competitors.topKeywords', 'Top Keywords')}
                    </h3>
                    <Button variant="ghost" size="sm" onClick={() => setActiveTab('pages')}>
                      Ver todas
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {(latestAnalysis?.top_keywords || []).slice(0, 5).map((kw: any, i: number) => (
                      <div key={i} className="p-3 bg-white dark:bg-gray-800 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <span className="w-6 h-6 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 rounded text-xs font-bold flex items-center justify-center">
                              {i + 1}
                            </span>
                            <span className="font-medium text-gray-900 dark:text-white">{kw.keyword}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-green-600 font-medium">#{kw.position}</span>
                            {(kw.source === 'google_ads' || kw.source === 'dataforseo_labs') && (
                              <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                                Real
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-gray-500 mt-1">
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {formatNumber(kw.volume)}/mês
                          </span>
                          {kw.cpc > 0 && (
                            <span className="flex items-center gap-1">
                              <MousePointer className="w-3 h-3" />
                              R$ {kw.cpc.toFixed(2)}
                            </span>
                          )}
                          {kw.competition && (
                            <span className={`px-1.5 py-0.5 rounded text-xs ${
                              kw.competition === 'HIGH' ? 'bg-red-100 text-red-700' :
                              kw.competition === 'MEDIUM' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-green-100 text-green-700'
                            }`}>
                              {kw.competition === 'HIGH' ? 'Alta' : kw.competition === 'MEDIUM' ? 'Média' : 'Baixa'}
                            </span>
                          )}
                          {kw.trend && kw.trend !== 'stable' && (
                            <span className={`flex items-center gap-0.5 ${
                              kw.trend === 'up' ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {kw.trend === 'up' ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                    {(!latestAnalysis?.top_keywords || latestAnalysis.top_keywords.length === 0) && (
                      <div className="text-center py-8 text-gray-500">
                        <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>Nenhuma keyword encontrada</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Top Pages */}
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 sm:p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    {t('competitors.topPages', 'Top Páginas')}
                  </h3>
                  <div className="space-y-3">
                    {(latestAnalysis?.top_pages || []).slice(0, 5).map((page: any, i: number) => (
                      <div key={i} className="p-3 bg-white dark:bg-gray-800 rounded-lg">
                        <div className="flex items-center justify-between">
                          <a
                            href={page.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-indigo-600 hover:underline text-sm truncate max-w-xs"
                          >
                            {page.url?.replace(`https://${competitor.domain}`, '') || '/'}
                          </a>
                          <span className="text-sm text-gray-500">
                            {formatNumber(page.traffic)} visitas
                          </span>
                        </div>
                        <div className="mt-2 w-full bg-gray-200 dark:bg-gray-600 rounded-full h-1.5">
                          <div
                            className="bg-indigo-600 h-1.5 rounded-full"
                            style={{ width: `${Math.min((page.traffic / (latestAnalysis?.top_pages?.[0]?.traffic || 1)) * 100, 100)}%` }}
                          />
                        </div>
                      </div>
                    ))}
                    {(!latestAnalysis?.top_pages || latestAnalysis.top_pages.length === 0) && (
                      <div className="text-center py-8 text-gray-500">
                        <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>Nenhuma página encontrada</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Insights & Recommendations */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-6">
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 sm:p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <Zap className="w-5 h-5 text-blue-600" />
                    {t('competitors.insights._title', 'Insights')}
                  </h3>
                  <div className="space-y-3 max-h-80 overflow-y-auto">
                    {(latestAnalysis?.insights || [])
                      .filter((insight: any) => {
                        // Filtrar insights relacionados a tráfego pago
                        const text = (insight.message_pt || insight.message || insight || '').toLowerCase();
                        const paidKeywords = ['pago', 'paid', 'ads', 'anúncio', 'anuncio', 'cpc', 'adwords', 'google ads'];
                        return !paidKeywords.some(kw => text.includes(kw));
                      })
                      .map((insight: any, i: number) => (
                      <div key={i} className={`flex items-start gap-3 p-3 rounded-lg ${
                        insight.type === 'strength' ? 'bg-green-100 dark:bg-green-900/30' :
                        insight.type === 'weakness' ? 'bg-red-100 dark:bg-red-900/30' :
                        'bg-white dark:bg-gray-800'
                      }`}>
                        {insight.type === 'strength' ? (
                          <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                        ) : insight.type === 'weakness' ? (
                          <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                        ) : (
                          <Info className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                        )}
                        <p className="text-sm text-gray-700 dark:text-gray-300">{insight.message_pt || insight}</p>
                      </div>
                    ))}
                    {(!latestAnalysis?.insights || latestAnalysis.insights.filter((insight: any) => {
                      const text = (insight.message_pt || insight.message || insight || '').toLowerCase();
                      const paidKeywords = ['pago', 'paid', 'ads', 'anúncio', 'anuncio', 'cpc', 'adwords', 'google ads'];
                      return !paidKeywords.some(kw => text.includes(kw));
                    }).length === 0) && (
                      <p className="text-gray-500 text-center py-4">Analise o concorrente para gerar insights</p>
                    )}
                  </div>
                </div>

                <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 sm:p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <Target className="w-5 h-5 text-green-600" />
                    {t('competitors.recommendations', 'Recomendações')}
                  </h3>
                  <div className="space-y-3 max-h-80 overflow-y-auto">
                    {(latestAnalysis?.recommendations || [])
                      .filter((rec: any) => {
                        // Filtrar recomendações relacionadas a tráfego pago
                        const text = (rec.message_pt || rec.message || rec || '').toLowerCase();
                        const paidKeywords = ['pago', 'paid', 'ads', 'anúncio', 'anuncio', 'cpc', 'adwords', 'google ads'];
                        return !paidKeywords.some(kw => text.includes(kw));
                      })
                      .map((rec: any, i: number) => (
                      <div key={i} className={`flex items-start gap-3 p-3 rounded-lg ${
                        rec.priority === 'high' ? 'bg-red-100 dark:bg-red-900/30 border-l-4 border-red-500' :
                        rec.priority === 'medium' ? 'bg-yellow-100 dark:bg-yellow-900/30 border-l-4 border-yellow-500' :
                        'bg-white dark:bg-gray-800 border-l-4 border-gray-300'
                      }`}>
                        <CheckCircle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                          rec.priority === 'high' ? 'text-red-500' :
                          rec.priority === 'medium' ? 'text-yellow-500' : 'text-green-500'
                        }`} />
                        <div>
                          {rec.priority && (
                            <span className={`text-xs font-medium uppercase ${
                              rec.priority === 'high' ? 'text-red-600' :
                              rec.priority === 'medium' ? 'text-yellow-600' : 'text-gray-500'
                            }`}>
                              {rec.priority === 'high' ? 'Alta Prioridade' : rec.priority === 'medium' ? 'Média Prioridade' : 'Baixa Prioridade'}
                            </span>
                          )}
                          <p className="text-sm text-gray-700 dark:text-gray-300">{rec.message_pt || rec}</p>
                        </div>
                      </div>
                    ))}
                    {(!latestAnalysis?.recommendations || latestAnalysis.recommendations.filter((rec: any) => {
                      const text = (rec.message_pt || rec.message || rec || '').toLowerCase();
                      const paidKeywords = ['pago', 'paid', 'ads', 'anúncio', 'anuncio', 'cpc', 'adwords', 'google ads'];
                      return !paidKeywords.some(kw => text.includes(kw));
                    }).length === 0) && (
                      <p className="text-gray-500 text-center py-4">Analise o concorrente para gerar recomendações</p>
                    )}
                  </div>
                </div>
              </div>

              {/* === SEÇÃO: PRESENÇA DE MARCA NO GOOGLE === */}
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-xl p-4 sm:p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <Award className="w-5 h-5 text-purple-600" />
                  Presença de Marca no Google
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Brand SERP Presence */}
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Globe className="w-4 h-4 text-purple-500" />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Posição para Marca</span>
                    </div>
                    {latestAnalysis?.brand_serp_presence?.owns_position_1 ? (
                      <div className="flex items-center gap-2">
                        <span className="text-3xl font-bold text-green-600">#1</span>
                        <CheckCircle className="w-6 h-6 text-green-500" />
                      </div>
                    ) : latestAnalysis?.brand_serp_presence?.total_positions > 0 ? (
                      <div className="flex items-center gap-2">
                        <span className="text-3xl font-bold text-yellow-600">#{latestAnalysis.brand_serp_presence.positions[0]}</span>
                        <AlertTriangle className="w-5 h-5 text-yellow-500" />
                      </div>
                    ) : (
                      <span className="text-gray-500 text-sm">Não analisado</span>
                    )}
                  </div>

                  {/* Knowledge Graph */}
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="w-4 h-4 text-indigo-500" />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Knowledge Panel</span>
                    </div>
                    {latestAnalysis?.knowledge_graph ? (
                      <div>
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-5 h-5 text-green-500" />
                          <span className="font-medium text-green-600">Possui</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1 truncate">{latestAnalysis.knowledge_graph.title}</p>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <XCircle className="w-5 h-5 text-gray-400" />
                        <span className="text-gray-500">Não possui</span>
                      </div>
                    )}
                  </div>

                  {/* Featured Snippet */}
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Eye className="w-4 h-4 text-blue-500" />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Featured Snippet</span>
                    </div>
                    {latestAnalysis?.featured_snippet ? (
                      <div>
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-5 h-5 text-green-500" />
                          <span className="font-medium text-green-600">Posição Zero</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1 truncate">{latestAnalysis.featured_snippet.title}</p>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <XCircle className="w-5 h-5 text-gray-400" />
                        <span className="text-gray-500">Não aparece</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* === SEÇÃO: O QUE AS PESSOAS PERGUNTAM === */}
              {latestAnalysis?.people_also_ask && latestAnalysis.people_also_ask.length > 0 && (
                <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 sm:p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <HelpCircle className="w-5 h-5 text-amber-600" />
                    As Pessoas Também Perguntam
                    <span className="text-xs bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200 px-2 py-0.5 rounded-full ml-2">
                      Ideias de Conteúdo
                    </span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {latestAnalysis.people_also_ask.slice(0, 8).map((question: any, i: number) => (
                      <div key={i} className="flex items-start gap-3 p-3 bg-white dark:bg-gray-800 rounded-lg hover:shadow-md transition-shadow">
                        <MessageCircle className="w-4 h-4 text-amber-500 mt-1 flex-shrink-0" />
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          {typeof question === 'string' ? question : question?.question || question?.value || JSON.stringify(question)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* === SEÇÃO: BUSCAS RELACIONADAS E AUTOCOMPLETE === */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-6">
                {/* Related Searches */}
                {latestAnalysis?.related_searches && latestAnalysis.related_searches.length > 0 && (
                  <div className="bg-cyan-50 dark:bg-cyan-900/20 rounded-xl p-4 sm:p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                      <Search className="w-5 h-5 text-cyan-600" />
                      Buscas Relacionadas
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {latestAnalysis.related_searches.slice(0, 10).map((search: any, i: number) => (
                        <span
                          key={i}
                          className="px-3 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-sm rounded-full border border-cyan-200 dark:border-cyan-800 hover:bg-cyan-100 dark:hover:bg-cyan-900/50 transition-colors cursor-pointer"
                        >
                          {typeof search === 'string' ? search : search?.query || search?.value || ''}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Autocomplete Suggestions */}
                {latestAnalysis?.autocomplete_suggestions && latestAnalysis.autocomplete_suggestions.length > 0 && (
                  <div className="bg-pink-50 dark:bg-pink-900/20 rounded-xl p-4 sm:p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                      <Hash className="w-5 h-5 text-pink-600" />
                      O que Buscam sobre a Marca
                    </h3>
                    <div className="space-y-2">
                      {latestAnalysis.autocomplete_suggestions.slice(0, 8).map((suggestion: any, i: number) => (
                        <div
                          key={i}
                          className="flex items-center gap-2 p-2 bg-white dark:bg-gray-800 rounded-lg text-sm"
                        >
                          <Search className="w-3 h-3 text-gray-400" />
                          <span className="text-gray-700 dark:text-gray-300">
                            {typeof suggestion === 'string' ? suggestion : suggestion?.value || suggestion?.query || ''}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* === SEÇÃO: NOTÍCIAS E MÍDIA === */}
              {latestAnalysis?.news_mentions && latestAnalysis.news_mentions.length > 0 && (
                <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-4 sm:p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <Newspaper className="w-5 h-5 text-emerald-600" />
                    Menções em Notícias
                    <span className="text-xs bg-emerald-200 dark:bg-emerald-800 text-emerald-800 dark:text-emerald-200 px-2 py-0.5 rounded-full ml-2">
                      {latestAnalysis.news_mentions.length} notícias
                    </span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {latestAnalysis.news_mentions.slice(0, 6).map((news: any, i: number) => (
                      <a
                        key={i}
                        href={news.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex flex-col p-4 bg-white dark:bg-gray-800 rounded-lg hover:shadow-md transition-shadow group"
                      >
                        <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                          <span className="font-medium text-emerald-600">{news.source}</span>
                          {news.date && <span>• {news.date}</span>}
                        </div>
                        <h4 className="font-medium text-gray-900 dark:text-white group-hover:text-emerald-600 transition-colors line-clamp-2">
                          {news.title}
                        </h4>
                        {news.snippet && (
                          <p className="text-xs text-gray-500 mt-2 line-clamp-2">{news.snippet}</p>
                        )}
                        <div className="flex items-center gap-1 mt-2 text-xs text-emerald-600">
                          <ExternalLink className="w-3 h-3" />
                          <span>Ler mais</span>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* === SEÇÃO: TOP STORIES (se houver) === */}
              {latestAnalysis?.top_stories && latestAnalysis.top_stories.length > 0 && (
                <div className="bg-orange-50 dark:bg-orange-900/20 rounded-xl p-4 sm:p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <Trending className="w-5 h-5 text-orange-600" />
                    Top Stories no Google
                    <span className="text-xs bg-orange-200 dark:bg-orange-800 text-orange-800 dark:text-orange-200 px-2 py-0.5 rounded-full ml-2">
                      Em Destaque
                    </span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {latestAnalysis.top_stories.slice(0, 3).map((story: any, i: number) => (
                      <a
                        key={i}
                        href={story.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex flex-col p-4 bg-white dark:bg-gray-800 rounded-lg hover:shadow-md transition-shadow group"
                      >
                        {story.imageUrl && (
                          <img
                            src={story.imageUrl}
                            alt={story.title}
                            className="w-full h-24 object-cover rounded-lg mb-3"
                          />
                        )}
                        <span className="text-xs text-orange-600 font-medium mb-1">{story.source}</span>
                        <h4 className="font-medium text-gray-900 dark:text-white group-hover:text-orange-600 transition-colors line-clamp-2 text-sm">
                          {story.title}
                        </h4>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Pages Tab */}
          {activeTab === 'pages' && (
            <div className="space-y-3 sm:space-y-6">
              {/* Summary Cards */}
              {latestAnalysis?.top_keywords && latestAnalysis.top_keywords.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  {/* Páginas Indexadas */}
                  <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl p-4 text-white">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="w-5 h-5" />
                      <span className="text-sm font-medium opacity-90">{t('competitors.pagesIndexed', 'Páginas Indexadas')}</span>
                    </div>
                    <div className="text-2xl font-bold">
                      {formatNumber(latestAnalysis.organic_keywords || latestAnalysis.top_keywords.length)}
                    </div>
                    <p className="text-xs mt-1 opacity-70">{t('competitors.pagesInGoogle', 'Páginas encontradas no Google')}</p>
                  </div>

                  {/* People Also Ask */}
                  <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl p-4 text-white">
                    <div className="flex items-center gap-2 mb-2">
                      <HelpCircle className="w-5 h-5" />
                      <span className="text-sm font-medium opacity-90">People Also Ask</span>
                    </div>
                    <div className="text-2xl font-bold">
                      {latestAnalysis.people_also_ask?.length || 0}
                    </div>
                    <p className="text-xs mt-1 opacity-70">{t('competitors.questionsFound', 'Perguntas relacionadas')}</p>
                  </div>

                  {/* Related Searches */}
                  <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-4 text-white">
                    <div className="flex items-center gap-2 mb-2">
                      <Search className="w-5 h-5" />
                      <span className="text-sm font-medium opacity-90">{t('competitors.relatedSearches', 'Buscas Relacionadas')}</span>
                    </div>
                    <div className="text-2xl font-bold">
                      {latestAnalysis.related_searches?.length || 0}
                    </div>
                    <p className="text-xs mt-1 opacity-70">{t('competitors.searchSuggestions', 'Sugestões de busca')}</p>
                  </div>
                </div>
              )}

              {/* Info Banner */}
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 flex items-center gap-3">
                <Info className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  {t('competitors.serpDataInfo', 'Dados obtidos via SERP do Google. Mostra as páginas indexadas e keywords extraídas dos títulos.')}
                </p>
              </div>

              {/* People Also Ask Section */}
              {latestAnalysis?.people_also_ask && latestAnalysis.people_also_ask.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <HelpCircle className="w-4 h-4" />
                    People Also Ask
                  </h4>
                  <div className="space-y-2">
                    {latestAnalysis.people_also_ask.slice(0, 5).map((q: any, i: number) => (
                      <div key={i} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <p className="font-medium text-gray-900 dark:text-white">{q.question}</p>
                        {q.snippet && <p className="text-sm text-gray-500 mt-1">{q.snippet}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Related Searches Section */}
              {latestAnalysis?.related_searches && latestAnalysis.related_searches.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <Search className="w-4 h-4" />
                    {t('competitors.relatedSearches', 'Buscas Relacionadas')}
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {latestAnalysis.related_searches.slice(0, 10).map((search: any, i: number) => (
                      <span key={i} className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full text-sm">
                        {typeof search === 'string' ? search : search.query}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Páginas Indexadas - Tabela Simplificada */}
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    {t('competitors.indexedPages', 'Páginas Indexadas')} ({latestAnalysis?.top_keywords?.length || 0})
                  </h4>
                </div>

                <div className="overflow-x-auto scrollbar-hide">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700">
                        <th className="pb-3 pr-2 sm:pr-4">{t('competitors.keyword', 'Keyword')}</th>
                        <th className="pb-3 px-2 sm:px-4">{t('competitors.position', 'Posição')}</th>
                        <th className="hidden sm:table-cell pb-3 px-4">URL</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {(latestAnalysis?.top_keywords || []).map((kw: any, i: number) => (
                        <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="py-3 pr-2 sm:pr-4 max-w-0 w-full sm:max-w-none sm:w-auto">
                            <span className="font-medium text-gray-900 dark:text-white break-words">{kw.keyword}</span>
                            {/* Celular: URL aparece embaixo da palavra (coluna URL some) */}
                            {kw.url && (
                              <a
                                href={kw.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="sm:hidden block text-xs text-indigo-600 hover:underline truncate"
                              >
                                {kw.url}
                              </a>
                            )}
                          </td>
                          <td className="py-3 px-2 sm:px-4">
                            <span className={`font-bold ${
                              kw.position <= 3 ? 'text-green-600' :
                              kw.position <= 10 ? 'text-blue-600' : 'text-gray-600'
                            }`}>#{kw.position}</span>
                          </td>
                          <td className="hidden sm:table-cell py-3 px-4">
                            {kw.url && (
                              <a
                                href={kw.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-indigo-600 hover:underline text-sm truncate block max-w-[200px] sm:max-w-[400px]"
                              >
                                {kw.url}
                              </a>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {(!latestAnalysis?.top_keywords || latestAnalysis.top_keywords.length === 0) && (
                    <div className="text-center py-12 text-gray-500">
                      <Search className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>{t('competitors.noPagesFound', 'Nenhuma página encontrada')}</p>
                      <p className="text-sm mt-1">{t('competitors.analyzeToDiscover', 'Analise o concorrente para descobrir suas páginas')}</p>
                    </div>
                  )}
                </div>
              </div>

            </div>
          )}

          {/* Backlinks Tab */}
          {activeTab === 'backlinks' && (
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-6">
                <div className="min-w-0">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {t('competitors.backlinkProfile', 'Perfil de Backlinks')}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {formatNumber(competitor.backlinks_count || 0)} backlinks {t('common.from', 'de')} {latestAnalysis?.referring_domains || 0} {t('competitors.uniqueDomains', 'domínios')}
                  </p>
                </div>
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                  <div className="relative flex-1 sm:flex-none min-w-0">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder={t('competitors.searchBacklink', 'Buscar domínio ou anchor...')}
                      value={backlinkSearch}
                      onChange={(e) => setBacklinkSearch(e.target.value)}
                      className="w-full pl-9 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                  {backlinkSearch && (
                    <Button variant="secondary" size="sm" onClick={() => setBacklinkSearch('')} aria-label={t('common.clear', 'Limpar')} className="flex-shrink-0 whitespace-nowrap">
                      <XCircle className="w-4 h-4 sm:mr-2" />
                      <span className="hidden sm:inline">{t('common.clear', 'Limpar')}</span>
                    </Button>
                  )}
                </div>
              </div>

              {/* Backlink Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4 mb-4 sm:mb-6">
                <div className="p-3 sm:p-4 min-w-0 bg-gray-50 dark:bg-gray-700/50 rounded-lg text-center">
                  <div className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">
                    {formatNumber(filteredBacklinks.filter(b => b.link_type === 'dofollow').length)}
                  </div>
                  <div className="text-xs text-gray-500 mt-1 truncate">{t('competitors.dofollow', 'Dofollow')}</div>
                </div>
                <div className="p-3 sm:p-4 min-w-0 bg-gray-50 dark:bg-gray-700/50 rounded-lg text-center">
                  <div className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">
                    {formatNumber(filteredBacklinks.filter(b => b.link_type === 'nofollow').length)}
                  </div>
                  <div className="text-xs text-gray-500 mt-1 truncate">{t('competitors.nofollow', 'Nofollow')}</div>
                </div>
                <div className="p-3 sm:p-4 min-w-0 bg-gray-50 dark:bg-gray-700/50 rounded-lg text-center">
                  <div className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">
                    {latestAnalysis?.referring_domains || 0}
                  </div>
                  <div className="text-xs text-gray-500 mt-1 truncate">{t('competitors.uniqueDomains', 'Domínios Únicos')}</div>
                </div>
                <div className="p-3 sm:p-4 min-w-0 bg-gray-50 dark:bg-gray-700/50 rounded-lg text-center">
                  <div className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">
                    {Math.round(filteredBacklinks.reduce((acc, b) => acc + (b.domain_authority || 0), 0) / (filteredBacklinks.length || 1))}
                  </div>
                  <div className="text-xs text-gray-500 mt-1 truncate">{t('competitors.avgDA', 'DA Médio')}</div>
                </div>
              </div>

              <div className="overflow-x-auto scrollbar-hide">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700">
                      <th className="pb-3 pr-4">{t('competitors.sourceDomain', 'Domínio de Origem')}</th>
                      <th className="pb-3 px-4">DA</th>
                      <th className="hidden sm:table-cell pb-3 px-4">{t('competitors.type', 'Tipo')}</th>
                      <th className="hidden sm:table-cell pb-3 px-4">{t('competitors.anchorText', 'Anchor Text')}</th>
                      <th className="hidden sm:table-cell pb-3 px-4">{t('competitors.targetPage', 'Página Destino')}</th>
                      <th className="hidden sm:table-cell pb-3 px-4">{t('competitors.discovered', 'Descoberto')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {filteredBacklinks.map((bl, i) => (
                      <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="py-3 pr-2 sm:pr-4">
                          <a
                            href={bl.source_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-indigo-600 hover:underline font-medium break-all"
                          >
                            {bl.source_domain}
                          </a>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`font-medium ${
                            bl.domain_authority >= 50 ? 'text-green-600' :
                            bl.domain_authority >= 30 ? 'text-yellow-600' : 'text-gray-600'
                          }`}>
                            {bl.domain_authority}
                          </span>
                        </td>
                        <td className="hidden sm:table-cell py-3 px-4">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            bl.link_type === 'dofollow'
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                              : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                          }`}>
                            {bl.link_type}
                          </span>
                        </td>
                        <td className="hidden sm:table-cell py-3 px-4 text-gray-600 dark:text-gray-400 max-w-[100px] sm:max-w-[200px] truncate">
                          {bl.anchor_text || '-'}
                        </td>
                        <td className="hidden sm:table-cell py-3 px-4">
                          <a
                            href={bl.target_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-indigo-600 hover:underline text-sm truncate max-w-[120px] sm:max-w-[200px] block"
                          >
                            {bl.target_url?.replace(`https://${competitor.domain}`, '') || '/'}
                          </a>
                        </td>
                        <td className="hidden sm:table-cell py-3 px-4 text-gray-500 text-sm">
                          {bl.first_seen ? new Date(bl.first_seen).toLocaleDateString('pt-BR') : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredBacklinks.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    <Link2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>{backlinkSearch ? t('common.noResults', 'Nenhum resultado encontrado') : t('competitors.noBacklinksFound', 'Nenhum backlink encontrado')}</p>
                    <p className="text-sm mt-1">{!backlinkSearch && t('competitors.analyzeBacklinksDesc', 'Analise o concorrente para descobrir seus backlinks')}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Ads Tab */}
          {activeTab === 'ads' && (
            <div className="space-y-3 sm:space-y-6">
              {/* Header */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {t('competitors.adsIntelligence', 'Inteligência de Anúncios')}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {t('competitors.adsIntelligenceDesc', 'Anúncios ativos e keywords pagas do concorrente')}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <select
                    value={selectedRegion}
                    onChange={(e) => setSelectedRegion(e.target.value)}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    {regions.map((region) => (
                      <option key={region.code} value={region.code}>
                        {region.flag} {region.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Platform Tabs */}
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-1.5">
                <div className="flex gap-1">
                  <button
                    onClick={() => setAdsPlatformTab('google')}
                    className={`flex-1 min-w-0 flex items-center justify-center gap-1.5 sm:gap-2 px-2 sm:px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                      adsPlatformTab === 'google'
                        ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-sm'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <Search className="w-4 h-4" />
                    Google Ads
                    {adsData && (
                      <span className={`px-1.5 py-0.5 rounded text-xs ${
                        adsPlatformTab === 'google' ? 'bg-white/20' : 'bg-gray-200 dark:bg-gray-600'
                      }`}>
                        {adsData.totalAdsFound || 0}
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => setAdsPlatformTab('meta')}
                    className={`flex-1 min-w-0 flex items-center justify-center gap-1.5 sm:gap-2 px-2 sm:px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                      adsPlatformTab === 'meta'
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-sm'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <Layers className="w-4 h-4" />
                    Meta Ads
                    {metaAdsData && (
                      <span className={`px-1.5 py-0.5 rounded text-xs ${
                        adsPlatformTab === 'meta' ? 'bg-white/20' : 'bg-gray-200 dark:bg-gray-600'
                      }`}>
                        {metaAdsData.totalAdsFound || 0}
                      </span>
                    )}
                  </button>
                </div>
              </div>

              {/* ===== GOOGLE ADS TAB ===== */}
              {adsPlatformTab === 'google' && (
                <div className="space-y-3 sm:space-y-6">
                  {/* Search Button */}
                  <div className="flex justify-end">
                    <Button onClick={() => loadAds()} disabled={isLoadingAds}>
                      {isLoadingAds ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          {t('common.loading', 'Carregando...')}
                        </>
                      ) : (
                        <>
                          <Search className="w-4 h-4 mr-2" />
                          {t('competitors.searchGoogleAds', 'Buscar Google Ads')}
                        </>
                      )}
                    </Button>
                  </div>

                  {/* Loading State */}
                  {isLoadingAds && (
                    <div className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-xl p-5 sm:p-8">
                      <div className="flex flex-col items-center justify-center">
                        <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center animate-pulse">
                          <Search className="w-8 h-8 text-white" />
                        </div>
                        <p className="text-gray-600 dark:text-gray-400 mt-4">
                          {t('competitors.searchingGoogleAds', 'Buscando anúncios no Google Ads...')}
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          {t('competitors.mayTake30Seconds', 'Isso pode levar até 30 segundos')}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Google Ads Results */}
                  {adsData && !isLoadingAds && (
                    <div className="space-y-3 sm:space-y-6">
                      {/* Summary Cards */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
                        <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-xl">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
                              <Megaphone className="w-5 h-5 text-orange-600" />
                            </div>
                            <div>
                              <p className="text-2xl font-bold text-gray-900 dark:text-white">{adsData.totalAdsFound}</p>
                              <p className="text-xs text-gray-500">{t('competitors.totalAds', 'Total de Anúncios')}</p>
                            </div>
                          </div>
                        </div>
                        <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-xl">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                              <Image className="w-5 h-5 text-green-600" />
                            </div>
                            <div>
                              <p className="text-2xl font-bold text-gray-900 dark:text-white">{adsData.adsCollected}</p>
                              <p className="text-xs text-gray-500">{t('competitors.collected', 'Coletados')}</p>
                            </div>
                          </div>
                        </div>
                        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                              <LayoutGrid className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                {Object.keys(adsData.formatDistribution || {}).length}
                              </p>
                              <p className="text-xs text-gray-500">{t('competitors.formats', 'Formatos')}</p>
                            </div>
                          </div>
                        </div>
                        <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-xl">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              adsData.hasActiveAds ? 'bg-green-100 dark:bg-green-900/30' : 'bg-gray-100 dark:bg-gray-700'
                            }`}>
                              {adsData.hasActiveAds ? (
                                <CheckCircle className="w-5 h-5 text-green-600" />
                              ) : (
                                <XCircle className="w-5 h-5 text-gray-400" />
                              )}
                            </div>
                            <div>
                              <p className="text-lg font-bold text-gray-900 dark:text-white">
                                {adsData.hasActiveAds ? t('competitors.active', 'Ativo') : t('competitors.inactive', 'Inativo')}
                              </p>
                              <p className="text-xs text-gray-500">{t('competitors.status', 'Status')}</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Advertiser Info */}
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                          <div>
                            <p className="text-sm text-gray-500">{t('competitors.advertiser', 'Anunciante')}</p>
                            <p className="font-semibold text-gray-900 dark:text-white">{adsData.advertiser}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                              adsData.hasActiveAds
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                            }`}>
                              {adsData.hasActiveAds ? t('competitors.advertising', 'Anunciando') : t('competitors.notAdvertising', 'Sem anúncios')}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Format Distribution */}
                      {adsData.formatDistribution && Object.keys(adsData.formatDistribution).length > 0 && (
                        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                          <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                            <PieChart className="w-4 h-4" />
                            {t('competitors.formatDistribution', 'Distribuição por Formato')}
                          </h4>
                          <div className="flex flex-wrap gap-3">
                            {Object.entries(adsData.formatDistribution).map(([format, count]) => (
                              <div
                                key={format}
                                className="flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg"
                              >
                                {format === 'leaderboard' && <RectangleHorizontal className="w-4 h-4 text-blue-500" />}
                                {format === 'square' && <Square className="w-4 h-4 text-green-500" />}
                                {format === 'rectangle' && <Columns className="w-4 h-4 text-orange-500" />}
                                {format === 'banner' && <RectangleHorizontal className="w-4 h-4 text-purple-500" />}
                                {format === 'skyscraper' && <Columns className="w-4 h-4 text-pink-500 rotate-90" />}
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300 capitalize">{format}</span>
                                <span className="text-sm text-gray-500">({count as number})</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Ads Grid */}
                      {(adsData.ads?.length || 0) > 0 ? (
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                            <Image className="w-4 h-4" />
                            {t('competitors.adCreatives', 'Criativos dos Anúncios')} ({adsData.ads?.length || 0}{adsData.totalAdsFound ? ` / ${adsData.totalAdsFound}` : ''})
                          </h4>
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            {(adsData.ads || []).map((ad, index) => (
                              <div
                                key={index}
                                className="group relative bg-gray-50 dark:bg-gray-800 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-all cursor-pointer"
                                onClick={() => setSelectedAd(ad)}
                              >
                                <div className="aspect-square relative">
                                  <img
                                    src={ad.imageUrl}
                                    alt={`Ad ${ad.index}`}
                                    className="w-full h-full object-contain bg-white"
                                    loading="lazy"
                                  />
                                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center">
                                    <Eye className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                  </div>
                                </div>
                                <div className="p-3">
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs font-medium text-gray-600 dark:text-gray-400 capitalize">
                                      {safeStr(ad.formatName) || safeStr(ad.format)}
                                    </span>
                                    <span className="text-xs text-gray-500">{safeStr(ad.dimensions)}</span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>

                        </div>
                      ) : (
                        <div className="text-center py-8 text-gray-500">
                          <Image className="w-12 h-12 mx-auto mb-3 opacity-30" />
                          <p>{t('competitors.noAdsCollected', 'Nenhum criativo coletado')}</p>
                        </div>
                      )}

                      {/* Botões: Carregar mais 20 (roxo) + Refazer SÓ Google.
                          - "Carregar mais" mostra quando há ads E hasMore !== false (inclui undefined legado).
                          - "Refazer" chama loadAds() (per-platform), NÃO handleAnalyze (que refaz tudo). */}
                      <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
                        {adsData.hasMore !== false && (adsData.ads?.length || 0) > 0 && (
                          <button
                            onClick={handleLoadMoreGoogleAds}
                            disabled={isLoadingMoreGoogle}
                            className="px-5 py-2.5 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
                          >
                            {isLoadingMoreGoogle ? (
                              <><Loader2 className="w-4 h-4 animate-spin" /> {t('competitors.loadingMore', 'Carregando...')}</>
                            ) : (
                              <><ChevronDown className="w-4 h-4" /> {t('competitors.loadMore20', 'Carregar mais 20')}</>
                            )}
                          </button>
                        )}
                        <button
                          onClick={() => loadAds()}
                          disabled={isLoadingAds}
                          className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2 border border-gray-200 dark:border-gray-600"
                        >
                          <RefreshCw className={`w-4 h-4 ${isLoadingAds ? 'animate-spin' : ''}`} />
                          {t('competitors.redoGoogleAds', 'Refazer Google Ads')}
                        </button>
                      </div>
                      {adsData.hasMore === false && (adsData.ads?.length || 0) > 20 && (
                        <p className="text-center text-xs text-gray-500 dark:text-gray-400 mt-3">
                          {t('competitors.allAdsLoaded', 'Todos os anúncios disponíveis foram carregados')}
                        </p>
                      )}

                      {/* Paid Keywords from Ads — fallback pra estimadas (OCR) quando
                          a Google Keyword Metrics API falha. Antes só mostrava as "reais"
                          com CPC>0 e escondia tudo se a API quebrasse. */}
                      {(() => {
                        const normalizeCpc = (cpc: number) => cpc > 1000 ? cpc / 1000000 : cpc;
                        const allKws = (adsData.paidKeywords || []).map((kw: any) => ({
                          ...kw,
                          cpc: normalizeCpc(Number(kw.cpc) || 0),
                          volume: Number(kw.volume) || 0,
                        }));
                        const realKeywords = allKws.filter((kw: any) => !kw.isEstimated && !kw.cpcError && kw.cpc > 0);
                        const estimatedKeywords = allKws.filter((kw: any) => kw.isEstimated || kw.cpcError || kw.cpc === 0);

                        // Se não tem nem real nem estimada, esconde
                        if (realKeywords.length === 0 && estimatedKeywords.length === 0) return null;

                        // Se só tem estimadas (API falhou), mostra bloco simples só com texto
                        if (realKeywords.length === 0 && estimatedKeywords.length > 0) {
                          return (
                            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                              <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                                <Hash className="w-4 h-4 text-blue-500" />
                                {t('competitors.extractedFromAds', 'Keywords extraídas dos anúncios')} ({estimatedKeywords.length})
                              </h4>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                                {t('competitors.estimatedKwsNotice', 'Texto extraído via OCR dos criativos. Métricas Google indisponíveis (sem volume/CPC).')}
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {estimatedKeywords.slice(0, 30).map((kw: any, i: number) => (
                                  <span key={i} className="px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 rounded-lg text-sm font-medium">
                                    {kw.keyword}
                                  </span>
                                ))}
                              </div>
                            </div>
                          );
                        }
                        const avgCpc = realKeywords.reduce((sum: number, kw: any) => sum + kw.cpc, 0) / realKeywords.length;
                        return (
                        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                          <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                            <Target className="w-4 h-4 text-orange-500" />
                            {t('competitors.paidKeywords', 'Keywords Pagas')} ({realKeywords.length})
                          </h4>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                            <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                              <p className="text-lg font-bold text-gray-900 dark:text-white">{realKeywords.length}</p>
                              <p className="text-xs text-gray-500">{t('competitors.totalPaidKeywords', 'Keywords')}</p>
                            </div>
                            <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                              <p className="text-lg font-bold text-gray-900 dark:text-white">
                                {formatNumber(realKeywords.reduce((sum: number, kw: any) => sum + (kw.volume || 0), 0))}
                              </p>
                              <p className="text-xs text-gray-500">{t('competitors.totalVolume', 'Volume Total')}</p>
                            </div>
                            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                              <p className="text-lg font-bold text-gray-900 dark:text-white">R$ {avgCpc.toFixed(2)}</p>
                              <p className="text-xs text-gray-500">{t('competitors.avgCpc', 'CPC Médio')}</p>
                            </div>
                            <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                              <p className="text-lg font-bold text-gray-900 dark:text-white">
                                R$ {formatCurrency(realKeywords.reduce((sum: number, kw: any) => sum + ((kw.volume || 0) * (kw.cpc || 0) * 0.05), 0))}
                              </p>
                              <p className="text-xs text-gray-500">{t('competitors.estimatedSpend', 'Gasto Est./mês')}</p>
                            </div>
                          </div>
                          <div className="overflow-x-auto scrollbar-hide">
                            <table className="w-full">
                              <thead>
                                <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700">
                                  <th className="pb-3 pr-4">{t('competitors.keyword', 'Keyword')}</th>
                                  <th className="pb-3 px-4">{t('competitors.volume', 'Volume')}</th>
                                  <th className="pb-3 px-4">CPC</th>
                                  <th className="hidden sm:table-cell pb-3 px-4">{t('competitors.competition', 'Competição')}</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                {realKeywords.slice(0, 15).map((kw: any, i: number) => (
                                  <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                    <td className="py-3 pr-4">
                                      <span className="font-medium text-gray-900 dark:text-white">{kw.keyword}</span>
                                    </td>
                                    <td className="py-3 px-4 text-gray-600 dark:text-gray-400">{formatNumber(kw.volume || 0)}</td>
                                    <td className="py-3 px-4">
                                      <span className="font-semibold text-green-600 dark:text-green-400">R$ {Number(kw.cpc).toFixed(2)}</span>
                                    </td>
                                    <td className="hidden sm:table-cell py-3 px-4">
                                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                        kw.competition === 'HIGH' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                                        kw.competition === 'MEDIUM' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                                        'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                      }`}>
                                        {kw.competition === 'HIGH' ? t('competitors.competitionHigh', 'Alta') :
                                         kw.competition === 'MEDIUM' ? t('competitors.competitionMedium', 'Média') :
                                         t('competitors.competitionLow', 'Baixa')}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* Google Empty State */}
                  {!adsData && !isLoadingAds && (
                    <div className="text-center py-12">
                      <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-cyan-100 dark:from-blue-900/30 dark:to-cyan-900/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <Search className="w-10 h-10 text-blue-500" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                        {t('competitors.discoverGoogleAds', 'Descubra os Google Ads do concorrente')}
                      </h3>
                      <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto mb-6">
                        {t('competitors.discoverGoogleAdsDesc', 'Busque anúncios ativos no Google Ads Transparency Center, keywords pagas e CPC.')}
                      </p>
                      <Button onClick={() => loadAds()} disabled={isLoadingAds}>
                        <Search className="w-4 h-4 mr-2" />
                        {t('competitors.searchGoogleAds', 'Buscar Google Ads')}
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* ===== META ADS TAB ===== */}
              {adsPlatformTab === 'meta' && (
                <div className="space-y-3 sm:space-y-6">
                  {/* Search Button */}
                  <div className="flex justify-end">
                    <Button onClick={() => loadMetaAds()} disabled={isLoadingMetaAds} className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
                      {isLoadingMetaAds ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          {t('common.loading', 'Carregando...')}
                        </>
                      ) : (
                        <>
                          <Layers className="w-4 h-4 mr-2" />
                          {t('competitors.searchMetaAds', 'Buscar Meta Ads')}
                        </>
                      )}
                    </Button>
                  </div>

                  {/* Loading State */}
                  {isLoadingMetaAds && (
                    <div className="bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl p-5 sm:p-8">
                      <div className="flex flex-col items-center justify-center">
                        <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center animate-pulse">
                          <Layers className="w-8 h-8 text-white" />
                        </div>
                        <p className="text-gray-600 dark:text-gray-400 mt-4">
                          {t('competitors.searchingMetaAds', 'Buscando anúncios no Meta Ad Library...')}
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          {t('competitors.mayTake45Seconds', 'Isso pode levar até 45 segundos')}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Meta Ads Results */}
                  {metaAdsData && !isLoadingMetaAds && (
                    <div className="space-y-3 sm:space-y-6">
                      {/* Summary Cards */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
                        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                              <Megaphone className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                              <p className="text-2xl font-bold text-gray-900 dark:text-white">{metaAdsData.totalAdsFound || 0}</p>
                              <p className="text-xs text-gray-500">{t('competitors.totalAds', 'Total de Anúncios')}</p>
                            </div>
                          </div>
                        </div>
                        <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-xl">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                              <Image className="w-5 h-5 text-purple-600" />
                            </div>
                            <div>
                              <p className="text-2xl font-bold text-gray-900 dark:text-white">{metaAdsData.adsCollected || 0}</p>
                              <p className="text-xs text-gray-500">{t('competitors.collected', 'Coletados')}</p>
                            </div>
                          </div>
                        </div>
                        <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-xl">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center">
                              <Layers className="w-5 h-5 text-indigo-600" />
                            </div>
                            <div>
                              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                {Object.keys(metaAdsData.platformDistribution || {}).length}
                              </p>
                              <p className="text-xs text-gray-500">{t('competitors.metaPlatforms', 'Plataformas')}</p>
                            </div>
                          </div>
                        </div>
                        <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-xl">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              metaAdsData.hasActiveAds ? 'bg-green-100 dark:bg-green-900/30' : 'bg-gray-100 dark:bg-gray-700'
                            }`}>
                              {metaAdsData.hasActiveAds ? (
                                <CheckCircle className="w-5 h-5 text-green-600" />
                              ) : (
                                <XCircle className="w-5 h-5 text-gray-400" />
                              )}
                            </div>
                            <div>
                              <p className="text-lg font-bold text-gray-900 dark:text-white">
                                {metaAdsData.hasActiveAds ? t('competitors.active', 'Ativo') : t('competitors.inactive', 'Inativo')}
                              </p>
                              <p className="text-xs text-gray-500">{t('competitors.status', 'Status')}</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Advertiser + Library Link */}
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                          <div>
                            <p className="text-sm text-gray-500">{t('competitors.advertiser', 'Anunciante')}</p>
                            <p className="font-semibold text-gray-900 dark:text-white">{metaAdsData.advertiser}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                              metaAdsData.hasActiveAds
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                            }`}>
                              {metaAdsData.hasActiveAds ? t('competitors.advertising', 'Anunciando') : t('competitors.notAdvertising', 'Sem anúncios')}
                            </span>
                            {metaAdsData.libraryUrl && (
                              <a
                                href={metaAdsData.libraryUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-2 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                                title={t('competitors.viewInMetaLibrary', 'Ver na Meta Ad Library')}
                              >
                                <ExternalLink className="w-4 h-4 text-gray-500" />
                              </a>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Platform & Format Distribution */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Platform Distribution */}
                        {metaAdsData.platformDistribution && Object.keys(metaAdsData.platformDistribution).length > 0 && (
                          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                            <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                              <Layers className="w-4 h-4" />
                              {t('competitors.platformDistribution', 'Distribuição por Plataforma')}
                            </h4>
                            <div className="space-y-2">
                              {Object.entries(metaAdsData.platformDistribution).map(([platform, count]) => {
                                const total = metaAdsData.adsCollected || 1;
                                const pct = Math.round(((count as number) / total) * 100);
                                const colors: Record<string, string> = {
                                  'Facebook': 'bg-blue-500',
                                  'Instagram': 'bg-gradient-to-r from-purple-500 to-pink-500',
                                  'Messenger': 'bg-blue-400',
                                  'Audience Network': 'bg-green-500'
                                };
                                return (
                                  <div key={platform}>
                                    <div className="flex items-center justify-between mb-1">
                                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{platform}</span>
                                      <span className="text-sm text-gray-500">{count as number} ({pct}%)</span>
                                    </div>
                                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                      <div className={`h-2 rounded-full ${colors[platform] || 'bg-gray-500'}`} style={{ width: `${pct}%` }} />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Format Distribution */}
                        {metaAdsData.formatDistribution && Object.keys(metaAdsData.formatDistribution).length > 0 && (
                          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                            <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                              <PieChart className="w-4 h-4" />
                              {t('competitors.formatDistribution', 'Distribuição por Formato')}
                            </h4>
                            <div className="space-y-2">
                              {Object.entries(metaAdsData.formatDistribution).map(([format, count]) => {
                                const total = metaAdsData.adsCollected || 1;
                                const pct = Math.round(((count as number) / total) * 100);
                                const icons: Record<string, React.ReactNode> = {
                                  'single_image': <Image className="w-4 h-4 text-blue-500" />,
                                  'video': <Video className="w-4 h-4 text-red-500" />,
                                  'carousel': <Layers className="w-4 h-4 text-purple-500" />
                                };
                                const labels: Record<string, string> = {
                                  'single_image': t('competitors.formatSingleImage', 'Imagem Única'),
                                  'video': t('competitors.formatVideo', 'Vídeo'),
                                  'carousel': t('competitors.formatCarousel', 'Carrossel')
                                };
                                return (
                                  <div key={format} className="flex items-center gap-3 px-3 py-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                    {icons[format] || <LayoutGrid className="w-4 h-4 text-gray-500" />}
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300 flex-1">{labels[format] || format}</span>
                                    <span className="text-sm text-gray-500">{count as number} ({pct}%)</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Strategy Analysis (AI-powered) */}
                      {metaAdsData.analysis?.hasData && (
                        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
                          <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                            <Brain className="w-5 h-5 text-purple-500" />
                            {t('competitors.strategyAnalysis', 'Análise de Estratégia')}
                            <span className="text-xs px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-full font-normal">AI</span>
                          </h4>

                          {/* Overall Strategy Summary */}
                          {metaAdsData.analysis.overallStrategy && (
                            <div className="mb-4 p-4 bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-xl">
                              <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{metaAdsData.analysis.overallStrategy}</p>
                            </div>
                          )}

                          {/* Stats Cards */}
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                            {/* Activity Level */}
                            <div className="p-4 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20">
                              <div className="flex items-center gap-2 mb-2">
                                <Activity className="w-4 h-4 text-blue-600" />
                                <span className="text-sm font-semibold text-gray-900 dark:text-white">
                                  {t('competitors.activityLevel', 'Nível de Atividade')}
                                </span>
                              </div>
                              <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${
                                metaAdsData.analysis.activityLevel === 'very_high' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                                metaAdsData.analysis.activityLevel === 'high' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' :
                                metaAdsData.analysis.activityLevel === 'medium' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                                'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                              }`}>
                                {metaAdsData.analysis.activityLevel === 'very_high' ? t('competitors.activityVeryHigh', 'Muito Alto') :
                                 metaAdsData.analysis.activityLevel === 'high' ? t('competitors.activityHigh', 'Alto') :
                                 metaAdsData.analysis.activityLevel === 'medium' ? t('competitors.activityMedium', 'Médio') :
                                 t('competitors.activityLow', 'Baixo')}
                              </span>
                              <p className="text-xs text-gray-500 mt-1">{metaAdsData.analysis.totalAds} {t('competitors.activeAdsCount', 'anúncios ativos')}</p>
                            </div>

                            {/* Dominant Format */}
                            {metaAdsData.analysis.dominantFormat && (
                              <div className="p-4 rounded-xl bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20">
                                <div className="flex items-center gap-2 mb-2">
                                  <LayoutGrid className="w-4 h-4 text-purple-600" />
                                  <span className="text-sm font-semibold text-gray-900 dark:text-white">
                                    {t('competitors.dominantFormat', 'Formato Dominante')}
                                  </span>
                                </div>
                                <p className="text-lg font-bold text-gray-900 dark:text-white capitalize">
                                  {metaAdsData.analysis.dominantFormat.format === 'single_image' ? t('competitors.formatSingleImage', 'Imagem Única') :
                                   metaAdsData.analysis.dominantFormat.format === 'video' ? t('competitors.formatVideo', 'Vídeo') :
                                   metaAdsData.analysis.dominantFormat.format === 'carousel' ? t('competitors.formatCarousel', 'Carrossel') :
                                   metaAdsData.analysis.dominantFormat.format}
                                </p>
                                <p className="text-xs text-gray-500">{metaAdsData.analysis.dominantFormat.percentage}% {t('competitors.ofAds', 'dos anúncios')}</p>
                              </div>
                            )}

                            {/* Estimated Audience */}
                            {metaAdsData.analysis.estimatedAudience && (
                              <div className="p-4 rounded-xl bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20">
                                <div className="flex items-center gap-2 mb-2">
                                  <Users className="w-4 h-4 text-green-600" />
                                  <span className="text-sm font-semibold text-gray-900 dark:text-white">
                                    {t('competitors.estimatedAudience', 'Público Estimado')}
                                  </span>
                                </div>
                                <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">{metaAdsData.analysis.estimatedAudience}</p>
                              </div>
                            )}
                          </div>

                          {/* Targeting Insights (from AI) */}
                          {metaAdsData.analysis.targetingInsights?.length > 0 && (
                            <div className="mt-4">
                              <h5 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                                <Crosshair className="w-4 h-4 text-orange-500" />
                                {t('competitors.targetingInsights', 'Insights de Segmentação')}
                              </h5>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {metaAdsData.analysis.targetingInsights.map((insight: any, i: number) => (
                                  <div key={i} className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                    <div className="w-8 h-8 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                                      <Crosshair className="w-4 h-4 text-orange-600" />
                                    </div>
                                    <div>
                                      <p className="text-sm font-medium text-gray-900 dark:text-white">{insight.label || insight.type}</p>
                                      <p className="text-xs text-gray-500 mt-0.5">{insight.detail}</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Messaging Themes (from AI) */}
                          {metaAdsData.analysis.messagingThemes?.length > 0 && (
                            <div className="mt-4">
                              <h5 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                                <MessageSquare className="w-4 h-4 text-blue-500" />
                                {t('competitors.messagingThemes', 'Temas de Mensagem')}
                              </h5>
                              <div className="space-y-2">
                                {metaAdsData.analysis.messagingThemes.map((theme: any, i: number) => {
                                  const themeColors: Record<string, string> = {
                                    urgency: 'border-l-red-500 bg-red-50 dark:bg-red-900/10',
                                    discount: 'border-l-green-500 bg-green-50 dark:bg-green-900/10',
                                    social_proof: 'border-l-blue-500 bg-blue-50 dark:bg-blue-900/10',
                                    exclusivity: 'border-l-purple-500 bg-purple-50 dark:bg-purple-900/10',
                                    benefit_focused: 'border-l-emerald-500 bg-emerald-50 dark:bg-emerald-900/10',
                                    emotional: 'border-l-pink-500 bg-pink-50 dark:bg-pink-900/10',
                                    educational: 'border-l-indigo-500 bg-indigo-50 dark:bg-indigo-900/10',
                                    new_launch: 'border-l-orange-500 bg-orange-50 dark:bg-orange-900/10',
                                    problem_solution: 'border-l-cyan-500 bg-cyan-50 dark:bg-cyan-900/10',
                                    authority: 'border-l-amber-500 bg-amber-50 dark:bg-amber-900/10',
                                    fomo: 'border-l-rose-500 bg-rose-50 dark:bg-rose-900/10',
                                    storytelling: 'border-l-violet-500 bg-violet-50 dark:bg-violet-900/10',
                                    comparison: 'border-l-teal-500 bg-teal-50 dark:bg-teal-900/10',
                                    testimonial: 'border-l-sky-500 bg-sky-50 dark:bg-sky-900/10'
                                  };
                                  return (
                                    <div key={i} className={`p-3 rounded-lg border-l-4 ${themeColors[theme.theme] || 'border-l-gray-500 bg-gray-50 dark:bg-gray-700/30'}`}>
                                      <p className="text-sm font-medium text-gray-900 dark:text-white">{theme.label || theme.theme}</p>
                                      {theme.explanation && (
                                        <p className="text-xs text-gray-500 mt-1">{theme.explanation}</p>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Top CTAs */}
                          {metaAdsData.analysis.topCtas?.length > 0 && (
                            <div className="mt-4">
                              <h5 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                                <MousePointerClick className="w-4 h-4 text-green-500" />
                                {t('competitors.topCtas', 'CTAs Mais Usados')}
                              </h5>
                              <div className="flex flex-wrap gap-2">
                                {metaAdsData.analysis.topCtas.map((cta: any, i: number) => (
                                  <div key={i} className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                    <span className="text-sm font-medium text-gray-900 dark:text-white capitalize">{cta.cta}</span>
                                    <span className="text-xs text-gray-500">({cta.percentage}%)</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Meta Ads Grid */}
                      {(metaAdsData.ads?.length || 0) > 0 ? (
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                            <Image className="w-4 h-4" />
                            {t('competitors.adCreatives', 'Criativos dos Anúncios')} ({metaAdsData.ads?.length || 0})
                          </h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {(metaAdsData.ads || []).map((ad: any, index: number) => (
                              <div
                                key={index}
                                className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-all"
                              >
                                {/* Ad Header - Page Name */}
                                {ad.pageName && (
                                  <div className="px-4 py-2.5 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2">
                                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
                                      <span className="text-white text-xs font-bold">{(safeStr(ad.pageName) || '?')[0]}</span>
                                    </div>
                                    <span className="text-sm font-semibold text-gray-900 dark:text-white truncate">{safeStr(ad.pageName)}</span>
                                  </div>
                                )}

                                {/* Ad Text */}
                                {(ad.headline || ad.description) && (
                                  <div className="px-4 py-3">
                                    {ad.description && (
                                      <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-3">{safeStr(ad.description)}</p>
                                    )}
                                  </div>
                                )}

                                {/* Ad Media (Video or Image) */}
                                {ad.format === 'video' && ad.videoUrls?.length > 0 ? (
                                  <div className="relative">
                                    <video
                                      src={ad.videoUrls[0]}
                                      poster={ad.imageUrl}
                                      controls
                                      preload="none"
                                      className="w-full h-48 object-cover bg-black"
                                    />
                                    <div className="absolute top-2 right-2 bg-black/60 rounded-full px-2 py-0.5 flex items-center gap-1">
                                      <Video className="w-3 h-3 text-white" />
                                      <span className="text-xs text-white font-medium">{t('competitors.formatVideo', 'Vídeo')}</span>
                                    </div>
                                  </div>
                                ) : ad.imageUrl ? (
                                  <div className="relative cursor-pointer group" onClick={() => setSelectedAd(ad)}>
                                    <img
                                      src={ad.imageUrl}
                                      alt={`Meta Ad ${ad.index}`}
                                      className="w-full h-48 object-cover"
                                      loading="lazy"
                                    />
                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center">
                                      <Eye className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                    </div>
                                    {ad.format === 'carousel' && (
                                      <div className="absolute top-2 right-2 bg-black/60 rounded-full p-1.5">
                                        <Layers className="w-4 h-4 text-white" />
                                      </div>
                                    )}
                                  </div>
                                ) : null}

                                {/* Ad Footer - Meta info */}
                                <div className="px-4 py-3 flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs font-medium text-gray-600 dark:text-gray-400 capitalize">
                                      {safeStr(ad.formatName) || safeStr(ad.format)}
                                    </span>
                                    {ad.platforms?.map((p: any, pi: number) => (
                                      <span key={pi} className="text-xs px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-gray-500">{safeStr(p)}</span>
                                    ))}
                                  </div>
                                  {ad.ctaText && (
                                    <span className="text-xs font-medium text-blue-600 dark:text-blue-400">{safeStr(ad.ctaText)}</span>
                                  )}
                                </div>

                                {/* Start Date */}
                                {ad.startDate && (
                                  <div className="px-4 pb-3">
                                    <span className="text-xs text-gray-400 flex items-center gap-1">
                                      <Calendar className="w-3 h-3" />
                                      {safeStr(ad.startDate)}
                                    </span>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>

                        </div>
                      ) : (
                        <div className="text-center py-8 text-gray-500">
                          <Image className="w-12 h-12 mx-auto mb-3 opacity-30" />
                          <p>{t('competitors.noMetaAdsCollected', 'Nenhum anúncio Meta coletado')}</p>
                        </div>
                      )}

                      {/* Botões Meta — Refazer per-platform (loadMetaAds, NÃO handleAnalyze) */}
                      <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
                        {metaAdsData.hasMore !== false && (metaAdsData.ads?.length || 0) > 0 && (
                          <button
                            onClick={handleLoadMoreMetaAds}
                            disabled={isLoadingMoreMeta}
                            className="px-5 py-2.5 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
                          >
                            {isLoadingMoreMeta ? (
                              <><Loader2 className="w-4 h-4 animate-spin" /> {t('competitors.loadingMore', 'Carregando...')}</>
                            ) : (
                              <><ChevronDown className="w-4 h-4" /> {t('competitors.loadMore20', 'Carregar mais 20')}</>
                            )}
                          </button>
                        )}
                        <button
                          onClick={() => loadMetaAds()}
                          disabled={isLoadingMetaAds}
                          className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2 border border-gray-200 dark:border-gray-600"
                        >
                          <RefreshCw className={`w-4 h-4 ${isLoadingMetaAds ? 'animate-spin' : ''}`} />
                          {t('competitors.redoMetaAds', 'Refazer Meta Ads')}
                        </button>
                      </div>
                      {metaAdsData.hasMore === false && (metaAdsData.ads?.length || 0) > 20 && (
                        <p className="text-center text-xs text-gray-500 dark:text-gray-400 mt-3">
                          {t('competitors.allAdsLoaded', 'Todos os anúncios disponíveis foram carregados')}
                        </p>
                      )}

                      {/* Extracted Keywords */}
                      {metaAdsData.extractedKeywords?.length > 0 && (
                        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                          <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                            <Hash className="w-4 h-4 text-blue-500" />
                            {t('competitors.extractedKeywords', 'Keywords Extraídas dos Anúncios')} ({metaAdsData.extractedKeywords.length})
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {metaAdsData.extractedKeywords.map((kw: string, i: number) => (
                              <span key={i} className="px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 rounded-lg text-sm font-medium">
                                {kw}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Meta Empty State */}
                  {!metaAdsData && !isLoadingMetaAds && (
                    <div className="text-center py-12">
                      <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <Layers className="w-10 h-10 text-purple-500" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                        {t('competitors.discoverMetaAds', 'Descubra os Meta Ads do concorrente')}
                      </h3>
                      <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto mb-6">
                        {t('competitors.discoverMetaAdsDesc', 'Busque anúncios ativos no Facebook e Instagram, análise de segmentação e estratégia de mídia.')}
                      </p>
                      <Button onClick={() => loadMetaAds()} disabled={isLoadingMetaAds} className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
                        <Layers className="w-4 h-4 mr-2" />
                        {t('competitors.searchMetaAds', 'Buscar Meta Ads')}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Site Audit Tab */}
          {activeTab === 'audit' && (
            <div className="space-y-3 sm:space-y-6">
              {/* Audit Header with Run Button */}
              <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl p-4 sm:p-6 text-white">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="text-lg font-semibold mb-1 flex items-center gap-2">
                      <Shield className="w-5 h-5" />
                      {t('competitors.siteAudit', 'Site Audit')}
                    </h3>
                    <p className="text-sm text-white/70">{t('competitors.technicalAnalysis', 'Análise técnica do site do concorrente')}</p>
                  </div>
                  <div className="flex items-center justify-between sm:justify-end gap-4">
                    {auditResult && (
                      <div className="sm:text-right whitespace-nowrap">
                        <div className="inline sm:block text-3xl sm:text-4xl font-bold">
                          {auditResult.overallScore || auditResult.pageSpeed?.mobile || '--'}
                        </div>
                        <span className="text-sm text-white/70">/100</span>
                      </div>
                    )}
                    <Button
                      onClick={runSiteAudit}
                      disabled={isLoadingAudit}
                      className="bg-white/20 hover:bg-white/30 text-white border-white/30 h-10 whitespace-nowrap"
                    >
                      {isLoadingAudit ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4 mr-2" />
                      )}
                      {t('competitors.runAudit', 'Executar Auditoria')}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Loading State */}
              {isLoadingAudit && (
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-12 text-center">
                  <Loader2 className="w-12 h-12 animate-spin text-emerald-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {t('competitors.auditRunning', 'Analisando site...')}
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    {t('competitors.auditRunningDesc', 'Isso pode levar alguns segundos')}
                  </p>
                </div>
              )}

              {/* Audit Results */}
              {auditResult && !isLoadingAudit && (
                <>
                  {/* Device Toggle */}
                  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-3">
                    <div className="flex items-center justify-center gap-2">
                      <span className="text-sm text-gray-500 dark:text-gray-400 mr-2">
                        {t('competitors.viewAs', 'Ver como')}:
                      </span>
                      <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                        <button
                          onClick={() => setAuditDevice('mobile')}
                          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                            auditDevice === 'mobile'
                              ? 'bg-white dark:bg-gray-600 text-emerald-600 dark:text-emerald-400 shadow-sm'
                              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                          }`}
                        >
                          <Zap className="w-4 h-4" />
                          Mobile
                        </button>
                        <button
                          onClick={() => setAuditDevice('desktop')}
                          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                            auditDevice === 'desktop'
                              ? 'bg-white dark:bg-gray-600 text-emerald-600 dark:text-emerald-400 shadow-sm'
                              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                          }`}
                        >
                          <Globe className="w-4 h-4" />
                          Desktop
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Scores Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
                    {/* Performance */}
                    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 text-center">
                      <div className="relative inline-flex items-center justify-center mb-2">
                        <svg className="w-20 h-20">
                          <circle
                            className="text-gray-200 dark:text-gray-700"
                            strokeWidth="6"
                            stroke="currentColor"
                            fill="transparent"
                            r="32"
                            cx="40"
                            cy="40"
                          />
                          <circle
                            className={`${
                              (auditDevice === 'mobile' ? auditResult.scores?.mobile?.performance : auditResult.scores?.desktop?.performance) >= 90 ? 'text-green-500' :
                              (auditDevice === 'mobile' ? auditResult.scores?.mobile?.performance : auditResult.scores?.desktop?.performance) >= 50 ? 'text-yellow-500' : 'text-red-500'
                            }`}
                            strokeWidth="6"
                            strokeDasharray={`${((auditDevice === 'mobile' ? auditResult.scores?.mobile?.performance : auditResult.scores?.desktop?.performance) || 0) * 2.01} 201`}
                            strokeLinecap="round"
                            stroke="currentColor"
                            fill="transparent"
                            r="32"
                            cx="40"
                            cy="40"
                            style={{ transform: 'rotate(-90deg)', transformOrigin: 'center' }}
                          />
                        </svg>
                        <span className={`absolute text-lg font-bold ${
                          (auditDevice === 'mobile' ? auditResult.scores?.mobile?.performance : auditResult.scores?.desktop?.performance) >= 90 ? 'text-green-600' :
                          (auditDevice === 'mobile' ? auditResult.scores?.mobile?.performance : auditResult.scores?.desktop?.performance) >= 50 ? 'text-yellow-600' : 'text-red-600'
                        }`}>
                          {(auditDevice === 'mobile' ? auditResult.scores?.mobile?.performance : auditResult.scores?.desktop?.performance) || 0}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Performance</p>
                    </div>

                    {/* SEO */}
                    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 text-center">
                      <div className="relative inline-flex items-center justify-center mb-2">
                        <svg className="w-20 h-20">
                          <circle className="text-gray-200 dark:text-gray-700" strokeWidth="6" stroke="currentColor" fill="transparent" r="32" cx="40" cy="40" />
                          <circle
                            className={`${
                              (auditDevice === 'mobile' ? auditResult.scores?.mobile?.seo : auditResult.scores?.desktop?.seo) >= 90 ? 'text-green-500' :
                              (auditDevice === 'mobile' ? auditResult.scores?.mobile?.seo : auditResult.scores?.desktop?.seo) >= 50 ? 'text-yellow-500' : 'text-red-500'
                            }`}
                            strokeWidth="6"
                            strokeDasharray={`${((auditDevice === 'mobile' ? auditResult.scores?.mobile?.seo : auditResult.scores?.desktop?.seo) || 0) * 2.01} 201`}
                            strokeLinecap="round"
                            stroke="currentColor"
                            fill="transparent"
                            r="32"
                            cx="40"
                            cy="40"
                            style={{ transform: 'rotate(-90deg)', transformOrigin: 'center' }}
                          />
                        </svg>
                        <span className={`absolute text-lg font-bold ${
                          (auditDevice === 'mobile' ? auditResult.scores?.mobile?.seo : auditResult.scores?.desktop?.seo) >= 90 ? 'text-green-600' :
                          (auditDevice === 'mobile' ? auditResult.scores?.mobile?.seo : auditResult.scores?.desktop?.seo) >= 50 ? 'text-yellow-600' : 'text-red-600'
                        }`}>
                          {(auditDevice === 'mobile' ? auditResult.scores?.mobile?.seo : auditResult.scores?.desktop?.seo) || 0}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">SEO</p>
                    </div>

                    {/* Accessibility */}
                    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 text-center">
                      <div className="relative inline-flex items-center justify-center mb-2">
                        <svg className="w-20 h-20">
                          <circle className="text-gray-200 dark:text-gray-700" strokeWidth="6" stroke="currentColor" fill="transparent" r="32" cx="40" cy="40" />
                          <circle
                            className={`${
                              (auditDevice === 'mobile' ? auditResult.scores?.mobile?.accessibility : auditResult.scores?.desktop?.accessibility) >= 90 ? 'text-green-500' :
                              (auditDevice === 'mobile' ? auditResult.scores?.mobile?.accessibility : auditResult.scores?.desktop?.accessibility) >= 50 ? 'text-yellow-500' : 'text-red-500'
                            }`}
                            strokeWidth="6"
                            strokeDasharray={`${((auditDevice === 'mobile' ? auditResult.scores?.mobile?.accessibility : auditResult.scores?.desktop?.accessibility) || 0) * 2.01} 201`}
                            strokeLinecap="round"
                            stroke="currentColor"
                            fill="transparent"
                            r="32"
                            cx="40"
                            cy="40"
                            style={{ transform: 'rotate(-90deg)', transformOrigin: 'center' }}
                          />
                        </svg>
                        <span className={`absolute text-lg font-bold ${
                          (auditDevice === 'mobile' ? auditResult.scores?.mobile?.accessibility : auditResult.scores?.desktop?.accessibility) >= 90 ? 'text-green-600' :
                          (auditDevice === 'mobile' ? auditResult.scores?.mobile?.accessibility : auditResult.scores?.desktop?.accessibility) >= 50 ? 'text-yellow-600' : 'text-red-600'
                        }`}>
                          {(auditDevice === 'mobile' ? auditResult.scores?.mobile?.accessibility : auditResult.scores?.desktop?.accessibility) || 0}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{t('competitors.accessibility', 'Acessibilidade')}</p>
                    </div>

                    {/* Best Practices */}
                    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 text-center">
                      <div className="relative inline-flex items-center justify-center mb-2">
                        <svg className="w-20 h-20">
                          <circle className="text-gray-200 dark:text-gray-700" strokeWidth="6" stroke="currentColor" fill="transparent" r="32" cx="40" cy="40" />
                          <circle
                            className={`${
                              (auditDevice === 'mobile' ? auditResult.scores?.mobile?.bestPractices : auditResult.scores?.desktop?.bestPractices) >= 90 ? 'text-green-500' :
                              (auditDevice === 'mobile' ? auditResult.scores?.mobile?.bestPractices : auditResult.scores?.desktop?.bestPractices) >= 50 ? 'text-yellow-500' : 'text-red-500'
                            }`}
                            strokeWidth="6"
                            strokeDasharray={`${((auditDevice === 'mobile' ? auditResult.scores?.mobile?.bestPractices : auditResult.scores?.desktop?.bestPractices) || 0) * 2.01} 201`}
                            strokeLinecap="round"
                            stroke="currentColor"
                            fill="transparent"
                            r="32"
                            cx="40"
                            cy="40"
                            style={{ transform: 'rotate(-90deg)', transformOrigin: 'center' }}
                          />
                        </svg>
                        <span className={`absolute text-lg font-bold ${
                          (auditDevice === 'mobile' ? auditResult.scores?.mobile?.bestPractices : auditResult.scores?.desktop?.bestPractices) >= 90 ? 'text-green-600' :
                          (auditDevice === 'mobile' ? auditResult.scores?.mobile?.bestPractices : auditResult.scores?.desktop?.bestPractices) >= 50 ? 'text-yellow-600' : 'text-red-600'
                        }`}>
                          {(auditDevice === 'mobile' ? auditResult.scores?.mobile?.bestPractices : auditResult.scores?.desktop?.bestPractices) || 0}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{t('competitors.bestPractices', 'Práticas')}</p>
                    </div>
                  </div>

                  {/* Page Speed Comparison */}
                  {auditResult.pageSpeed && (
                    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                        {t('competitors.pageSpeed', 'Page Speed')}
                      </h4>
                      <div className="grid grid-cols-2 gap-3 sm:gap-6">
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-2">
                              <Zap className="w-4 h-4" />
                              Mobile
                            </span>
                            <span className={`font-bold ${
                              auditResult.pageSpeed.mobile >= 90 ? 'text-green-600' :
                              auditResult.pageSpeed.mobile >= 50 ? 'text-yellow-600' : 'text-red-600'
                            }`}>
                              {auditResult.pageSpeed.mobile}
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full ${
                                auditResult.pageSpeed.mobile >= 90 ? 'bg-green-500' :
                                auditResult.pageSpeed.mobile >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${auditResult.pageSpeed.mobile}%` }}
                            />
                          </div>
                        </div>
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-2">
                              <Globe className="w-4 h-4" />
                              Desktop
                            </span>
                            <span className={`font-bold ${
                              auditResult.pageSpeed.desktop >= 90 ? 'text-green-600' :
                              auditResult.pageSpeed.desktop >= 50 ? 'text-yellow-600' : 'text-red-600'
                            }`}>
                              {auditResult.pageSpeed.desktop}
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full ${
                                auditResult.pageSpeed.desktop >= 90 ? 'bg-green-500' :
                                auditResult.pageSpeed.desktop >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${auditResult.pageSpeed.desktop}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Core Web Vitals */}
                  {auditResult.coreWebVitals && (
                    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                        <Zap className="w-5 h-5 text-yellow-500" />
                        Core Web Vitals
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {auditResult.coreWebVitals.lcp && (
                          <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                            <p className="font-medium text-gray-900 dark:text-white">LCP</p>
                            <p className="text-xs text-gray-500 mb-2">Largest Contentful Paint</p>
                            <p className={`font-bold ${
                              auditResult.coreWebVitals.lcp.score >= 90 ? 'text-green-600' :
                              auditResult.coreWebVitals.lcp.score >= 50 ? 'text-yellow-600' : 'text-red-600'
                            }`}>
                              {auditResult.coreWebVitals.lcp.displayValue}
                            </p>
                          </div>
                        )}
                        {auditResult.coreWebVitals.fid && (
                          <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                            <p className="font-medium text-gray-900 dark:text-white">FID</p>
                            <p className="text-xs text-gray-500 mb-2">First Input Delay</p>
                            <p className={`font-bold ${
                              auditResult.coreWebVitals.fid.score >= 90 ? 'text-green-600' :
                              auditResult.coreWebVitals.fid.score >= 50 ? 'text-yellow-600' : 'text-red-600'
                            }`}>
                              {auditResult.coreWebVitals.fid.displayValue}
                            </p>
                          </div>
                        )}
                        {auditResult.coreWebVitals.cls && (
                          <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                            <p className="font-medium text-gray-900 dark:text-white">CLS</p>
                            <p className="text-xs text-gray-500 mb-2">Cumulative Layout Shift</p>
                            <p className={`font-bold ${
                              auditResult.coreWebVitals.cls.score >= 90 ? 'text-green-600' :
                              auditResult.coreWebVitals.cls.score >= 50 ? 'text-yellow-600' : 'text-red-600'
                            }`}>
                              {auditResult.coreWebVitals.cls.displayValue}
                            </p>
                          </div>
                        )}
                        {auditResult.coreWebVitals.ttfb && (
                          <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                            <p className="font-medium text-gray-900 dark:text-white">TTFB</p>
                            <p className="text-xs text-gray-500 mb-2">Time to First Byte</p>
                            <p className={`font-bold ${
                              auditResult.coreWebVitals.ttfb.score >= 90 ? 'text-green-600' :
                              auditResult.coreWebVitals.ttfb.score >= 50 ? 'text-yellow-600' : 'text-red-600'
                            }`}>
                              {auditResult.coreWebVitals.ttfb.displayValue}
                            </p>
                          </div>
                        )}
                        {auditResult.coreWebVitals.speedIndex && (
                          <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                            <p className="font-medium text-gray-900 dark:text-white">Speed Index</p>
                            <p className="text-xs text-gray-500 mb-2">{t('competitors.visualSpeed', 'Velocidade Visual')}</p>
                            <p className={`font-bold ${
                              auditResult.coreWebVitals.speedIndex.score >= 90 ? 'text-green-600' :
                              auditResult.coreWebVitals.speedIndex.score >= 50 ? 'text-yellow-600' : 'text-red-600'
                            }`}>
                              {auditResult.coreWebVitals.speedIndex.displayValue}
                            </p>
                          </div>
                        )}
                        {auditResult.coreWebVitals.tbt && (
                          <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                            <p className="font-medium text-gray-900 dark:text-white">TBT</p>
                            <p className="text-xs text-gray-500 mb-2">Total Blocking Time</p>
                            <p className={`font-bold ${
                              auditResult.coreWebVitals.tbt.score >= 90 ? 'text-green-600' :
                              auditResult.coreWebVitals.tbt.score >= 50 ? 'text-yellow-600' : 'text-red-600'
                            }`}>
                              {auditResult.coreWebVitals.tbt.displayValue}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Issues Summary */}
                  {auditResult.totalIssues && (
                    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                        {t('competitors.issuesSummary', 'Resumo de Issues')}
                      </h4>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
                        <div className="text-center">
                          <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center mx-auto mb-2">
                            <XCircle className="w-6 h-6 text-red-500" />
                          </div>
                          <p className="text-2xl font-bold text-red-600">{auditResult.totalIssues.critical}</p>
                          <p className="text-xs text-gray-500">{t('competitors.critical', 'Críticos')}</p>
                        </div>
                        <div className="text-center">
                          <div className="w-12 h-12 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg flex items-center justify-center mx-auto mb-2">
                            <AlertTriangle className="w-6 h-6 text-yellow-500" />
                          </div>
                          <p className="text-2xl font-bold text-yellow-600">{auditResult.totalIssues.warning}</p>
                          <p className="text-xs text-gray-500">{t('competitors.warnings', 'Avisos')}</p>
                        </div>
                        <div className="text-center">
                          <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mx-auto mb-2">
                            <Info className="w-6 h-6 text-blue-500" />
                          </div>
                          <p className="text-2xl font-bold text-blue-600">{auditResult.totalIssues.info}</p>
                          <p className="text-xs text-gray-500">{t('competitors.notices', 'Avisos')}</p>
                        </div>
                        <div className="text-center">
                          <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center mx-auto mb-2">
                            <CheckCircle className="w-6 h-6 text-green-500" />
                          </div>
                          <p className="text-2xl font-bold text-green-600">{auditResult.totalIssues.passed}</p>
                          <p className="text-xs text-gray-500">{t('competitors.passed', 'Passou')}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Screenshot and Additional Vitals */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Screenshot */}
                    {(auditResult.screenshot?.final || auditResult.mobile?.screenshot?.final || auditResult.desktop?.screenshot?.final) && (
                      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                          <Image className="w-5 h-5" />
                          {t('siteAudit.screenshot', 'Screenshot do Site')}
                        </h4>
                        <div className="relative bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden">
                          <img
                            src={
                              auditDevice === 'mobile'
                                ? (auditResult.mobile?.screenshot?.final || auditResult.screenshot?.final)
                                : (auditResult.desktop?.screenshot?.final || auditResult.screenshot?.final)
                            }
                            alt={t('siteAudit.screenshotAlt', 'Screenshot do site')}
                            className="w-full h-auto rounded-lg shadow-inner"
                          />
                          {((auditDevice === 'mobile' ? auditResult.mobile?.screenshot?.timing : auditResult.desktop?.screenshot?.timing) || auditResult.screenshot?.timing) && (
                            <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                              {(((auditDevice === 'mobile' ? auditResult.mobile?.screenshot?.timing : auditResult.desktop?.screenshot?.timing) || auditResult.screenshot?.timing) / 1000).toFixed(1)}s
                            </div>
                          )}
                        </div>
                        {/* Thumbnails Timeline */}
                        {((auditDevice === 'mobile' ? auditResult.mobile?.screenshot?.thumbnails : auditResult.desktop?.screenshot?.thumbnails) || auditResult.screenshot?.thumbnails)?.length > 0 && (
                          <div className="mt-4">
                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                              {t('siteAudit.loadingProgress', 'Progresso de Carregamento')}
                            </p>
                            <div className="flex gap-1 overflow-x-auto pb-2">
                              {((auditDevice === 'mobile' ? auditResult.mobile?.screenshot?.thumbnails : auditResult.desktop?.screenshot?.thumbnails) || auditResult.screenshot?.thumbnails)?.slice(0, 10).map((thumb: any, idx: number) => (
                                <div key={idx} className="flex-shrink-0 text-center">
                                  <img
                                    src={thumb.data}
                                    alt={`Frame ${idx + 1}`}
                                    className="w-16 h-28 object-cover rounded border border-gray-200 dark:border-gray-700"
                                  />
                                  <span className="text-xs text-gray-400">
                                    {(thumb.timing / 1000).toFixed(1)}s
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* FCP metric if not shown in vitals */}
                    {auditResult.coreWebVitals?.fcp && (
                      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                          <Zap className="w-5 h-5 text-yellow-500" />
                          {t('siteAudit.additionalMetrics', 'Métricas Adicionais')}
                        </h4>
                        <div className="space-y-4">
                          <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">FCP</p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">First Contentful Paint</p>
                            </div>
                            <div className="text-right">
                              <p className={`font-bold ${getScoreColor(auditResult.coreWebVitals.fcp.score)}`}>
                                {auditResult.coreWebVitals.fcp.displayValue}
                              </p>
                              <div className="w-16 h-1.5 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full ${getScoreBgColor(auditResult.coreWebVitals.fcp.score)}`}
                                  style={{ width: `${auditResult.coreWebVitals.fcp.score}%` }}
                                />
                              </div>
                            </div>
                          </div>
                          {auditResult.coreWebVitals?.tti && (
                            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                              <div>
                                <p className="font-medium text-gray-900 dark:text-white">TTI</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">Time to Interactive</p>
                              </div>
                              <div className="text-right">
                                <p className={`font-bold ${getScoreColor(auditResult.coreWebVitals.tti.score)}`}>
                                  {auditResult.coreWebVitals.tti.displayValue}
                                </p>
                                <div className="w-16 h-1.5 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                                  <div
                                    className={`h-full rounded-full ${getScoreBgColor(auditResult.coreWebVitals.tti.score)}`}
                                    style={{ width: `${auditResult.coreWebVitals.tti.score}%` }}
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        {auditResult.lighthouseVersion && (
                          <p className="text-xs text-gray-400 mt-4 text-center">
                            {t('siteAudit.poweredBy', 'Powered by')} Lighthouse {auditResult.lighthouseVersion}
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* SEO Analysis */}
                  {auditResult.seoAnalysis && (
                    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
                        <FileText className="w-5 h-5" />
                        {t('siteAudit.seoAnalysis', 'Análise SEO')}
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
                        {/* Meta Tags */}
                        <div className="space-y-4">
                          <h5 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                            <Code className="w-4 h-4" />
                            {t('siteAudit.metaTags', 'Meta Tags')}
                          </h5>
                          <div className="space-y-3">
                            <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Title</span>
                                <Badge variant={auditResult.seoAnalysis.meta.titleLength >= 30 && auditResult.seoAnalysis.meta.titleLength <= 60 ? 'success' : 'warning'}>
                                  {auditResult.seoAnalysis.meta.titleLength} chars
                                </Badge>
                              </div>
                              <p className="text-sm text-gray-600 dark:text-gray-400 break-words">
                                {auditResult.seoAnalysis.meta.title || <span className="text-red-500">{t('siteAudit.missing', 'Faltando')}</span>}
                              </p>
                            </div>
                            <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Meta Description</span>
                                <Badge variant={auditResult.seoAnalysis.meta.descriptionLength >= 120 && auditResult.seoAnalysis.meta.descriptionLength <= 160 ? 'success' : 'warning'}>
                                  {auditResult.seoAnalysis.meta.descriptionLength} chars
                                </Badge>
                              </div>
                              <p className="text-sm text-gray-600 dark:text-gray-400 break-words">
                                {auditResult.seoAnalysis.meta.description || <span className="text-red-500">{t('siteAudit.missing', 'Faltando')}</span>}
                              </p>
                            </div>
                            {auditResult.seoAnalysis.meta.canonical && (
                              <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Canonical</span>
                                <p className="text-sm text-gray-600 dark:text-gray-400 break-all">
                                  {auditResult.seoAnalysis.meta.canonical}
                                </p>
                              </div>
                            )}
                            {(auditResult.seoAnalysis.meta.ogTitle || auditResult.seoAnalysis.meta.ogDescription) && (
                              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                                <span className="text-sm font-medium text-blue-700 dark:text-blue-300 flex items-center gap-1">
                                  Open Graph <CheckCircle className="w-3 h-3" />
                                </span>
                                {auditResult.seoAnalysis.meta.ogTitle && (
                                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                    <strong>og:title:</strong> {auditResult.seoAnalysis.meta.ogTitle}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Headings Structure */}
                        <div className="space-y-4">
                          <h5 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            {t('siteAudit.headingsStructure', 'Estrutura de Headings')}
                          </h5>
                          <div className="space-y-2">
                            <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700/50 rounded">
                              <span className="text-sm font-mono">H1</span>
                              <Badge variant={auditResult.seoAnalysis.headings.h1Count === 1 ? 'success' : auditResult.seoAnalysis.headings.h1Count === 0 ? 'danger' : 'warning'}>
                                {auditResult.seoAnalysis.headings.h1Count}
                              </Badge>
                            </div>
                            <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700/50 rounded">
                              <span className="text-sm font-mono">H2</span>
                              <Badge variant="primary">{auditResult.seoAnalysis.headings.h2Count}</Badge>
                            </div>
                            <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700/50 rounded">
                              <span className="text-sm font-mono">H3</span>
                              <Badge variant="primary">{auditResult.seoAnalysis.headings.h3Count}</Badge>
                            </div>
                            <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700/50 rounded">
                              <span className="text-sm font-mono">H4</span>
                              <Badge variant="primary">{auditResult.seoAnalysis.headings.h4Count}</Badge>
                            </div>
                          </div>
                          {auditResult.seoAnalysis.headings.h1.length > 0 && (
                            <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                              <span className="text-sm font-medium text-green-700 dark:text-green-300">H1 Content:</span>
                              {auditResult.seoAnalysis.headings.h1.map((h1: string, i: number) => (
                                <p key={i} className="text-sm text-gray-600 dark:text-gray-400 mt-1">"{h1}"</p>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Content & Links */}
                        <div className="space-y-4">
                          <h5 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                            <Link2 className="w-4 h-4" />
                            {t('siteAudit.contentLinks', 'Conteúdo & Links')}
                          </h5>
                          <div className="space-y-3">
                            <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                              <div className="grid grid-cols-2 gap-4 text-center">
                                <div>
                                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                    {auditResult.seoAnalysis.content.wordCount}
                                  </p>
                                  <p className="text-xs text-gray-500">{t('siteAudit.words', 'Palavras')}</p>
                                </div>
                                <div>
                                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                    {auditResult.seoAnalysis.content.textToHtmlRatio}%
                                  </p>
                                  <p className="text-xs text-gray-500">{t('siteAudit.textRatio', 'Ratio Texto')}</p>
                                </div>
                              </div>
                            </div>
                            <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                              <div className="grid grid-cols-2 gap-4 text-center">
                                <div>
                                  <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                                    {auditResult.seoAnalysis.links.internalCount}
                                  </p>
                                  <p className="text-xs text-gray-500">{t('siteAudit.internalLinks', 'Links Internos')}</p>
                                </div>
                                <div>
                                  <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                                    {auditResult.seoAnalysis.links.externalCount}
                                  </p>
                                  <p className="text-xs text-gray-500">{t('siteAudit.externalLinks', 'Links Externos')}</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Images */}
                        <div className="space-y-4">
                          <h5 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                            <Image className="w-4 h-4" />
                            {t('siteAudit.images', 'Imagens')}
                          </h5>
                          <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 text-center">
                              <div>
                                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                  {auditResult.seoAnalysis.images.total}
                                </p>
                                <p className="text-xs text-gray-500">{t('common.total', 'Total')}</p>
                              </div>
                              <div>
                                <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                                  {auditResult.seoAnalysis.images.withAlt}
                                </p>
                                <p className="text-xs text-gray-500">{t('siteAudit.withAlt', 'Com Alt')}</p>
                              </div>
                              <div>
                                <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                                  {auditResult.seoAnalysis.images.withoutAlt}
                                </p>
                                <p className="text-xs text-gray-500">{t('siteAudit.withoutAlt', 'Sem Alt')}</p>
                              </div>
                            </div>
                          </div>
                          {auditResult.seoAnalysis.images.withoutAlt > 0 && (
                            <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                              <span className="text-sm font-medium text-red-700 dark:text-red-300">
                                {auditResult.seoAnalysis.images.withoutAlt} {t('siteAudit.imagesNeedAlt', 'imagens precisam de atributo alt')}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Technical Checks */}
                        <div className="space-y-4">
                          <h5 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                            <Shield className="w-4 h-4" />
                            {t('siteAudit.technicalChecks', 'Verificações Técnicas')}
                          </h5>
                          <div className="space-y-2">
                            {[
                              { key: 'usesHttps', label: 'HTTPS', value: auditResult.seoAnalysis.technical.usesHttps },
                              { key: 'hasViewport', label: 'Viewport Meta', value: auditResult.seoAnalysis.technical.hasViewport },
                              { key: 'hasCharset', label: 'Charset', value: auditResult.seoAnalysis.technical.hasCharset },
                              { key: 'hasLangAttribute', label: 'Lang Attribute', value: auditResult.seoAnalysis.technical.hasLangAttribute },
                              { key: 'hasStructuredData', label: 'Structured Data', value: auditResult.seoAnalysis.technical.hasStructuredData },
                              { key: 'hasFavicon', label: 'Favicon', value: auditResult.seoAnalysis.technical.hasFavicon },
                            ].map(check => (
                              <div key={check.key} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700/50 rounded">
                                <span className="text-sm text-gray-700 dark:text-gray-300">{check.label}</span>
                                {check.value ? (
                                  <CheckCircle className="w-5 h-5 text-green-500" />
                                ) : (
                                  <XCircle className="w-5 h-5 text-red-500" />
                                )}
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* SEO Issues */}
                        {auditResult.seoAnalysis.issues && auditResult.seoAnalysis.issues.length > 0 && (
                          <div className="space-y-4">
                            <h5 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                              <AlertTriangle className="w-4 h-4" />
                              {t('siteAudit.seoIssues', 'Problemas SEO')} ({auditResult.seoAnalysis.issues.length})
                            </h5>
                            <div className="space-y-2 max-h-64 overflow-y-auto">
                              {auditResult.seoAnalysis.issues.map((issue: any, idx: number) => (
                                <div
                                  key={idx}
                                  className={`p-2 rounded flex items-start gap-2 ${
                                    issue.type === 'error'
                                      ? 'bg-red-50 dark:bg-red-900/20'
                                      : issue.type === 'warning'
                                      ? 'bg-yellow-50 dark:bg-yellow-900/20'
                                      : 'bg-blue-50 dark:bg-blue-900/20'
                                  }`}
                                >
                                  {issue.type === 'error' ? (
                                    <XCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                                  ) : issue.type === 'warning' ? (
                                    <AlertTriangle className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                                  ) : (
                                    <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                                  )}
                                  <span className="text-sm text-gray-700 dark:text-gray-300">{issue.message}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Load Time & Score */}
                      <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                        <div className="flex items-center gap-3 sm:gap-6 text-sm text-gray-500 dark:text-gray-400">
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {t('siteAudit.loadTime', 'Tempo de carga')}: {auditResult.seoAnalysis.loadTime}ms
                          </span>
                          <span>
                            {t('siteAudit.pageSize', 'Tamanho')}: {(auditResult.seoAnalysis.contentLength / 1024).toFixed(1)} KB
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-500 dark:text-gray-400">{t('siteAudit.seoScore', 'Score SEO')}:</span>
                          <span className={`text-2xl font-bold ${getScoreColor(auditResult.seoAnalysis.score)}`}>
                            {auditResult.seoAnalysis.score}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Categories with Issues */}
                  {auditResult.categories && auditResult.categories.length > 0 && (
                    <div className="space-y-4">
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {t('siteAudit.detailedIssues', 'Issues Detalhados por Categoria')}
                      </h4>
                      {auditResult.categories.map((category: AuditCategory) => (
                        <div key={category.name} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                          <div
                            className="px-3 sm:px-6 py-2 sm:py-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50"
                            onClick={() => toggleCategory(category.name)}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                                  {category.name === 'SEO' || category.name === 'SEO On-Page' ? <FileText className="w-5 h-5" /> :
                                   category.name === 'Performance' ? <Zap className="w-5 h-5" /> :
                                   category.name === 'Links' ? <Link2 className="w-5 h-5" /> :
                                   category.name === 'Security' || category.name === 'Seguranca' ? <Shield className="w-5 h-5" /> :
                                   category.name === 'Accessibility' || category.name === 'Mobile' ? <Smartphone className="w-5 h-5" /> :
                                   <Code className="w-5 h-5" />}
                                </div>
                                <div>
                                  <h5 className="font-medium text-gray-900 dark:text-white">
                                    {category.name}
                                  </h5>
                                  <p className="text-sm text-gray-500 dark:text-gray-400">
                                    {category.issues?.filter((i: AuditIssue) => i.severity !== 'passed').length || 0} {t('siteAudit.issues', 'issues')} |{' '}
                                    {category.issues?.filter((i: AuditIssue) => i.severity === 'passed').length || 0} {t('siteAudit.passed', 'OK')}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-4">
                                <div className="text-right">
                                  <span className={`text-2xl font-bold ${getScoreColor(category.score)}`}>
                                    {category.score}
                                  </span>
                                  <span className="text-gray-400">/100</span>
                                </div>
                                {expandedCategories.has(category.name) ? (
                                  <ChevronDown className="w-5 h-5 text-gray-400" />
                                ) : (
                                  <ChevronRight className="w-5 h-5 text-gray-400" />
                                )}
                              </div>
                            </div>
                          </div>

                          {expandedCategories.has(category.name) && category.issues && (
                            <div className="px-6 pb-6 pt-0 border-t border-gray-200 dark:border-gray-700">
                              <div className="space-y-4 mt-4">
                                {category.issues.map((issue: AuditIssue) => (
                                  <div
                                    key={issue.id}
                                    className={`p-4 rounded-lg border ${
                                      issue.severity === 'critical'
                                        ? 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20'
                                        : issue.severity === 'warning'
                                        ? 'border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-900/20'
                                        : issue.severity === 'info'
                                        ? 'border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20'
                                        : 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20'
                                    }`}
                                  >
                                    <div className="flex items-start gap-3">
                                      {getSeverityIcon(issue.severity)}
                                      <div className="flex-1">
                                        <div className="flex items-center justify-between mb-1">
                                          <h6 className="font-medium text-gray-900 dark:text-white">
                                            {issue.title}
                                          </h6>
                                          <Badge variant={
                                            issue.severity === 'critical' ? 'danger' :
                                            issue.severity === 'warning' ? 'warning' :
                                            issue.severity === 'info' ? 'primary' : 'success'
                                          }>
                                            {issue.severity === 'critical' ? t('siteAudit.critical', 'Crítico') :
                                             issue.severity === 'warning' ? t('siteAudit.warning', 'Aviso') :
                                             issue.severity === 'info' ? t('siteAudit.info', 'Info') :
                                             t('siteAudit.passed', 'OK')}
                                          </Badge>
                                        </div>
                                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                          {issue.description}
                                        </p>
                                        {issue.recommendation && (
                                          <p className="text-sm text-gray-700 dark:text-gray-300">
                                            <strong>{t('siteAudit.recommendation', 'Recomendação')}:</strong> {issue.recommendation}
                                          </p>
                                        )}
                                        {issue.affectedUrls && issue.affectedUrls.length > 0 && (
                                          <div className="mt-2">
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                                              {t('siteAudit.affectedUrls', 'URLs afetadas')}:
                                            </p>
                                            <div className="flex flex-wrap gap-2">
                                              {issue.affectedUrls.slice(0, 3).map((u: string, i: number) => (
                                                <code
                                                  key={i}
                                                  className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded"
                                                >
                                                  {u}
                                                </code>
                                              ))}
                                              {issue.affectedUrls.length > 3 && (
                                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                                  +{issue.affectedUrls.length - 3} {t('common.more', 'mais')}
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Audit Date */}
                  {auditResult.auditedAt && (
                    <p className="text-xs text-gray-400 text-center">
                      {t('competitors.lastAuditedAt', 'Última auditoria')}: {new Date(auditResult.auditedAt).toLocaleString()}
                    </p>
                  )}
                </>
              )}

              {/* No Audit Data - Show Run Button */}
              {!auditResult && !isLoadingAudit && (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-gradient-to-br from-emerald-100 to-teal-100 dark:from-emerald-900/30 dark:to-teal-900/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Shield className="w-10 h-10 text-emerald-500" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {t('competitors.noAuditData', 'Nenhuma auditoria disponível')}
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto mb-6">
                    {t('competitors.runAnalysisForAudit', 'Execute uma auditoria para obter dados técnicos detalhados do site')}
                  </p>
                  <Button onClick={runSiteAudit} disabled={isLoadingAudit}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    {t('competitors.runAudit', 'Executar Auditoria')}
                  </Button>
                </div>
              )}
            </div>
          )}

        </div>
      </div>

      {/* Ad Preview Modal */}
      {selectedAd && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setSelectedAd(null)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-sm font-medium text-gray-700 dark:text-gray-300 capitalize">
                  {safeStr((selectedAd as any).formatName) || safeStr(selectedAd.format)}
                </span>
                {(selectedAd as any).dimensions && (
                  <span className="text-sm text-gray-500">{safeStr((selectedAd as any).dimensions)}</span>
                )}
                {(selectedAd as any).platforms?.map((p: string, pi: number) => (
                  <span key={pi} className="text-xs px-1.5 py-0.5 bg-blue-50 dark:bg-blue-900/30 rounded text-blue-600 dark:text-blue-400">{p}</span>
                ))}
              </div>
              <div className="flex items-center gap-2">
                {selectedAd.imageUrl && (
                  <a
                    href={selectedAd.imageUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    <ExternalLink className="w-5 h-5 text-gray-500" />
                  </a>
                )}
                <button
                  onClick={() => setSelectedAd(null)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-2xl leading-none"
                >
                  &times;
                </button>
              </div>
            </div>
            <div className="p-4 sm:p-6 overflow-auto max-h-[calc(90vh-80px)] space-y-4">
              {/* Meta ad header */}
              {((selectedAd as any).pageName || (selectedAd as any).description) && (
                <div className="space-y-3">
                  {(selectedAd as any).pageName && (
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
                        <span className="text-white text-sm font-bold">{((selectedAd as any).pageName || '?')[0]}</span>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 dark:text-white">{(selectedAd as any).pageName}</p>
                        {(selectedAd as any).startDate && (
                          <p className="text-xs text-gray-500 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {(selectedAd as any).startDate}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                  {(selectedAd as any).description && (
                    <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{(selectedAd as any).description}</p>
                  )}
                </div>
              )}
              {/* Video or Image */}
              {(selectedAd as any).format === 'video' && (selectedAd as any).videoUrls?.length > 0 ? (
                <video
                  src={(selectedAd as any).videoUrls[0]}
                  poster={selectedAd.imageUrl}
                  controls
                  autoPlay
                  className="max-w-full h-auto mx-auto rounded-lg shadow-lg bg-black"
                />
              ) : selectedAd.imageUrl ? (
                <img
                  src={selectedAd.imageUrl}
                  alt={`Ad ${selectedAd.index}`}
                  className="max-w-full h-auto mx-auto rounded-lg shadow-lg"
                />
              ) : null}
              {/* Headline (link preview style) */}
              {(selectedAd as any).headline && (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">{(selectedAd as any).headline}</p>
                </div>
              )}
              {/* CTA - real link to Meta Ad Library */}
              {(selectedAd as any).ctaText && (
                <div className="flex justify-center">
                  {metaAdsData?.libraryUrl ? (
                    <a
                      href={metaAdsData.libraryUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                    >
                      {(selectedAd as any).ctaText}
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  ) : (
                    <span className="px-6 py-2.5 bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 rounded-lg text-sm font-medium">
                      {(selectedAd as any).ctaText}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompetitorDetailPage;
